/**
 * `/data/:entity` page — phase 13 data browser + phase 14 row-edit modal.
 *
 * Left rail: entity picker (one button per slug in `BROWSABLE_ENTITIES`,
 * `apps/dashboard/src/repositories/entity-browser.ts`, served over
 * `/api/catalog`; vertical button list). Right pane: a `<table>` rendered from the response
 * `rows` with column headers derived from `Object.keys(rows[0])`.
 *
 * PII columns are SHOWN by default; the "Hide PII" toggle masks
 * them per-page-session.
 *
 * Phase 13 ships client-side PII redaction only — the endpoint
 * returns full row data and the frontend hides the values. Server-
 * side filter + reveal-audit-logging via a canonical action is
 * deferred row #19; future phases land that.
 *
 * Phase 14 — clicking a row in an entity that carries an
 * `ENTITY_UPDATE_ACTIONS[entity]` mapping opens an in-place edit
 * overlay modal. The modal dispatches the registered canonical
 * action(s) declared for that entity in `ENTITY_UPDATE_ACTIONS`
 * (`../lib/entity-actions`) through `/api/dispatch`
 * via the existing `useDispatch` hook; on success, the page
 * refetches the current cursor so the row reflects the new values.
 * The PII toggle and toolbar buttons stop event propagation so
 * they never trigger a row-click.
 *
 * Cursor pagination on UUID v7 monotonic id (or `occurred_at` for
 * `audit_log` / `first_seen_at` for `idempotency_seen` / `last_synced_at`
 * for `vendor_links`). The cursor is NOT exposed as pages: the table is one
 * continuously-growing list that keeps appending the next batch as the
 * operator scrolls, until `next_cursor === null` — the real end of the table.
 * Prev / Next buttons were removed on 2026-07-29.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  NavLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import type { DataResponse } from "@layer/dashboard/types";
import { MEMBER_GENDER_OPTIONS } from "@layer/canonical-data/member-profile-gaps";
import { isPlaceholderEmail } from "@layer/canonical-data/placeholder-email";
import {
  api,
  ApiError,
  dataRowDelete,
  dataRowDeleteAll,
  dataRowDeleteMany,
  type DataRowDeleteResponse,
  type DeleteDependent,
  type DeleteKept,
  type DevResetResponse,
  type MemberPurgeResponse,
} from "../lib/api";
import { ENTITY_UPDATE_ACTIONS } from "../lib/entity-actions";
import { isDoubleTap, type TapCandidate } from "../lib/double-tap";
import { useCursorFeed } from "../lib/use-cursor-feed";
import { useDispatch } from "../lib/use-dispatch";
import {
  useBrowsableEntities,
  isNonTableView,
} from "../lib/use-browsable-entities";
import { useIsMobile } from "../lib/use-is-mobile";
import { useRealtime } from "../lib/realtime";
import { clearCache, registerClearListener } from "../lib/resource-cache";
import { CollapsibleJson } from "../components/json-tree";
import { ActionModalButton } from "../components/ActionModalButton";
import { LoadMoreSentinel } from "../components/LoadMoreSentinel";
import { DiscountCodesPanel } from "../components/DiscountCodesPanel";
import { studioFormat } from "../lib/studio-tz";

const STUDIO_TIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

const ISO_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

const PAGE_LIMIT = 25;

/**
 * Mirror of `DELETE_ALL_PROTECTED` in
 * `apps/dashboard/src/repositories/entity-delete.ts` — the tables whose
 * "Delete all" button is not offered at all.
 *
 * The SERVER is the enforcement point (it rejects `mode:"all"` on these with
 * `entity_delete_all_protected`); this list only decides whether to render a
 * button that would be refused. A drift here is a cosmetic bug, never a
 * safety one, and `apps/dashboard/tests/entity-delete-bulk.test.ts` pins the two lists equal.
 */
const DELETE_ALL_PROTECTED_UI: ReadonlySet<string> = new Set([
  "studios",
  "trainers",
  "studio_secrets",
  "staff_allowlist",
  "studio_config",
  // Joined 2026-08-06: `/data` deletes are audited into `audit_log`, so
  // emptying it from here erases the delete trail. `Reset DB` still does.
  "audit_log",
]);

const PII_PLACEHOLDER = "•••";

// Realtime is the fast path; this slow poll is the safety net for a dropped
// socket / blocked Realtime (was 3s unconditional polling before migration 041).
const SAFETY_POLL_MS = 30_000;

// A change to any of these tables alters the `members` computed columns
// (credits, bookings, spend, trainer split, message counts, chat thread …),
// so a signal on them must refetch the members table too — not just a signal
// on `members` itself.
const MEMBERS_FEEDER_ENTITIES: ReadonlySet<string> = new Set([
  "bookings",
  "slots",
  "payments",
  "member_packages",
  "member_memberships",
  "member_flows",
  "inbound_messages",
  "outbound_messages",
  "resend_message_events",
]);

// Upper bound on the client-side page cache (below). Each distinct query
// signature (entity + view + cursor + filters + search + date range) is one
// entry; switching tabs / paging / searching adds entries. 80 covers a heavy
// browsing session with negligible memory; the oldest entry is evicted past it.
const PAGE_CACHE_MAX = 80;

/**
 * Backdrop-dismiss handlers for a modal that closes on an outside click.
 *
 * Naively wiring `onClick={onClose}` on the backdrop closes the modal whenever
 * a `click` lands on it — including the synthetic click that fires when you
 * start a text selection INSIDE the card, drag the cursor out, and release on
 * the backdrop (the browser dispatches that click on the nearest common
 * ancestor, i.e. the backdrop, so the card's `stopPropagation` never runs).
 * Selecting a phone number and letting go outside the popup would then close it.
 *
 * Fix: only dismiss on a TRUE outside click — the press AND the release both on
 * the backdrop itself. We record whether `mousedown` started on the backdrop
 * and only call `onClose` if the subsequent `click` also targets the backdrop.
 * A press that begins inside the card never arms the flag, so releasing on the
 * backdrop (text-selection drag-out) leaves the modal open.
 */
function useBackdropDismiss(onClose: () => void): {
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
} {
  const pressedOnBackdrop = useRef(false);
  return {
    onMouseDown: (e: React.MouseEvent): void => {
      pressedOnBackdrop.current = e.target === e.currentTarget;
    },
    onClick: (e: React.MouseEvent): void => {
      if (pressedOnBackdrop.current && e.target === e.currentTarget) {
        onClose();
      }
      pressedOnBackdrop.current = false;
    },
  };
}

/**
 * Identity of a browse QUERY — everything that changes which rows the server
 * returns: entity + scope + null-filters + optional fields + search + dates +
 * order. The cursor is deliberately NOT part of it, because scrolling further
 * down is the same query, not a different one. It is both the feed's
 * generation key (`useCursorFeed`) and the `dataPageCache` key. Fields are
 * URI-encoded before the pipe-join so a `|` inside a value cannot collide two
 * queries (the `auditQueryIdentity` discipline on Home).
 *
 * Exported so the invariant that matters can be pinned by a test: a background
 * refresh (30s safety poll, Realtime signal, post-edit refetch) must NOT change
 * this string, while a filter / sort / entity change must. The scrolled-in
 * tail resets exactly when this changes, so if a re-fetch could alter it the
 * list would collapse back to 25 rows mid-scroll.
 */
export function browseQueryIdentity(args: {
  readonly entity: string;
  readonly configScope: string;
  readonly nullFiltersKey: string;
  readonly extraFieldsKey: string;
  readonly search: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly order: string;
}): string {
  return [
    args.entity,
    args.configScope,
    args.nullFiltersKey,
    args.extraFieldsKey,
    args.search,
    args.dateFrom,
    args.dateTo,
    args.order,
  ]
    .map(encodeURIComponent)
    .join("|");
}

/**
 * MODULE-LEVEL page cache (NOT a per-component `useRef`, which is dropped
 * when `DataInner` unmounts on leaving `/data` — the cause of "every tab
 * reloads when I come back to Data"). Living at module scope, it survives
 * route changes within one SPA session exactly like the global
 * `resource-cache` store, so returning to `/data` paints the last-seen tab
 * instantly. Cleared on sign-out / stale-session via `registerClearListener`
 * (session isolation — no cross-studio leak after re-login). The feed still
 * revalidates every visit, so this only removes the blank-then-load flash,
 * never freshness — a cached page is the feed's SEED: painted on the first
 * frame, never trusted for paging (`useCursorFeed`).
 */
const dataPageCache = new Map<string, DataResponse>();
registerClearListener(() => dataPageCache.clear());

/**
 * Insert a fetched first page at the most-recent position (LRU eviction past
 * `PAGE_CACHE_MAX`). Called from the feed's fetcher for EVERY first page that
 * lands — including one the feed then drops as superseded, since the rows are
 * still valid for their key and a return visit can paint from them.
 */
function rememberPage(key: string, page: DataResponse): void {
  dataPageCache.delete(key);
  dataPageCache.set(key, page);
  if (dataPageCache.size > PAGE_CACHE_MAX) {
    const oldest = dataPageCache.keys().next().value;
    if (oldest !== undefined) dataPageCache.delete(oldest);
  }
}

export function Data(): JSX.Element {
  const { entity } = useParams<{ entity: string }>();
  const { entities, error: entitiesError } = useBrowsableEntities();

  if (!entity) {
    return <Navigate to="/data/members" replace />;
  }
  if (entitiesError) {
    return <div className="empty">Error loading catalog: {entitiesError}</div>;
  }
  if (entities === null) {
    return <div className="empty">Loading…</div>;
  }
  if (!entities.includes(entity)) {
    return <Navigate to="/data/members" replace />;
  }

  // PR contacts get a rich profile at /pr/people/:id (header + full
  // cross-channel thread + reply composer), so a row click opens it — the same
  // navigate-on-click the standalone /members view uses. Other entities keep the
  // in-place edit modal.
  const rowClickTo =
    entity === "outreach_targets"
      ? (row: Record<string, unknown>): string =>
          `/pr/people/${String(row["id"] ?? "")}`
      : undefined;

  return (
    <DataInner
      entity={entity}
      entities={entities}
      title="Data"
      {...(rowClickTo ? { rowClickTo } : {})}
    />
  );
}

/**
 * `/members` — the data-browser members table as a standalone, full-screen
 * quick-access page (nav-ordered between Tasks and PR). Renders the SAME
 * table as `/data/members` — the affordances carry over: column
 * drag-reorder / resize, the Columns + Add-field menus, per-row delete,
 * free-text search, and double-click-to-copy — except a row click navigates
 * to the member profile (`/members/:id`) instead of the edit modal, and the
 * left entity/views rail is hidden so the table fills the width. Ordered by
 * Codex registration number (`?order=registration`): members are sorted by
 * their Codex registration # DESC, with members lacking one floated to the top
 * by `created_at` — so the newest registrations sit on top and the Codex block
 * keeps its true registration order below (the `/data` browser keeps its
 * import-time `id` order).
 */
export function Members(): JSX.Element {
  const { entities, error: entitiesError } = useBrowsableEntities();

  if (entitiesError) {
    return <div className="empty">Error loading catalog: {entitiesError}</div>;
  }
  if (entities === null) {
    return <div className="empty">Loading…</div>;
  }
  if (!entities.includes("members")) {
    return <Navigate to="/data/members" replace />;
  }

  return (
    <DataInner
      entity="members"
      entities={entities}
      showSidebar={false}
      orderBy="registration"
      title="Members"
      rowClickTo={(row) => `/members/${String(row["id"] ?? "")}`}
    />
  );
}

interface DataInnerProps {
  readonly entity: string;
  readonly entities: ReadonlyArray<string>;
  /**
   * Show the left entity/views rail. The standalone `/members` page passes
   * false to render the table full-width with no entity picker. Default true.
   */
  readonly showSidebar?: boolean;
  /**
   * Optional server-side ordering override (`?order=`), e.g. `registration` for
   * the members Codex-registration sort. Folded into the page-cache key so it
   * never collides with the default `/data/<entity>` ordering.
   */
  readonly orderBy?: string;
  /**
   * When set, a row click NAVIGATES to this route instead of opening the
   * row-edit modal — the standalone `/members` page passes
   * `(row) => /members/<id>` so clicking anywhere on a row opens that member's
   * profile (the `/data/<entity>` browser keeps the in-place edit modal).
   */
  readonly rowClickTo?: (row: Record<string, unknown>) => string;
  /**
   * The `page-title` heading above the browser, so these two pages carry the
   * same masthead-to-content rhythm as every other page. `/data/:entity`
   * passes a constant — the entity being browsed is already named by the rail
   * and the table's own caption, so the title names the PAGE, not the table.
   */
  readonly title: string;
}

function DataInner({
  entity,
  entities,
  showSidebar = true,
  orderBy,
  rowClickTo,
  title,
}: DataInnerProps): JSX.Element {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // C1 — grouping + labels for the rail. Same cached `/api/catalog` resource
  // the parent already resolved, so this costs no extra request.
  const { groups: entityGroupMap, labels, groupOrder } = useBrowsableEntities();
  const [entityFilter, setEntityFilter] = useState("");
  // Grouped + filtered rail rows. Search matches the HUMAN label or the raw
  // slug, because an operator may know either. Entities the server hasn't
  // grouped fall into a trailing "Other" bucket rather than vanishing — the
  // type check makes that unreachable today, but a stale cached catalog
  // response is not a reason to hide a table.
  const entityGroups = useMemo(() => {
    const q = entityFilter.trim().toLowerCase();
    const matches = entities.filter((e) => {
      if (q.length === 0) return true;
      return (
        e.toLowerCase().includes(q) ||
        (labels[e] ?? "").toLowerCase().includes(q)
      );
    });
    const order = groupOrder.length > 0 ? groupOrder : [];
    const byGroup = new Map<string, string[]>();
    for (const e of matches) {
      const g = entityGroupMap[e] ?? "Other";
      const list = byGroup.get(g);
      if (list === undefined) byGroup.set(g, [e]);
      else list.push(e);
    }
    const named = order
      .filter((g) => byGroup.has(g))
      .map((g) => ({ name: g, entities: byGroup.get(g) ?? [] }));
    const leftovers = [...byGroup.keys()]
      .filter((g) => !order.includes(g))
      .sort()
      .map((g) => ({ name: g, entities: byGroup.get(g) ?? [] }));
    const all = [...named, ...leftovers];
    // No grouping data at all (stale cached catalog) → one unlabelled group,
    // i.e. exactly the flat list this replaced. Degrade, never blank.
    if (all.length === 0 && matches.length > 0) {
      return [{ name: "", entities: matches }];
    }
    return all;
  }, [entities, entityFilter, entityGroupMap, labels, groupOrder]);
  // Mobile decluttering applies ONLY to the standalone quick-access page
  // (`/members`, `showSidebar={false}`) — the phone's Members tab. It strips
  // the date filters + the whole table-chrome toolbar (meta line, row-height,
  // Hide-additional / Hide-PII toggles, Columns / Add-field / Reset menus, and
  // the drag hint — none usable on a touchscreen) and forces additional columns
  // hidden, leaving just Add-member + Search + the table. The `/data/:entity`
  // browser (sidebar) and every desktop viewport are untouched; this is a
  // render-only coercion, so no persisted layout state changes.
  const declutter = isMobile && !showSidebar;
  // Desktop Members (`showSidebar={false}`) collapses its table chrome into one
  // "+" menu sharing a row with Add Member and the filters. `/data/:entity`
  // keeps the full strip — its caption and toggles earn their space when you
  // are browsing arbitrary tables. Mobile already has `declutter`, which strips
  // the same chrome outright.
  const compactTools = !showSidebar && !declutter;
  const [searchParams] = useSearchParams();
  // Phase 38 — extract per-entity `?<col>=null` filters from the URL.
  // The endpoint validates against `NULL_FILTER_COLUMNS`; the SPA
  // just round-trips the params unchanged. NULL filters drive the
  // orphan-payment inbox at `/data/payments?member_id=null`.
  const nullFilters: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (value === "null" && key !== "cursor" && key !== "limit") {
      nullFilters[key] = "null";
    }
  });
  const nullFilterKeys = Object.keys(nullFilters);
  const hasNullFilter = nullFilterKeys.length > 0;
  const nullFiltersKey = nullFilterKeys.slice().sort().join(",");
  // Saved-view mode — `?view=<id>` namespaces this table's column layout +
  // add-fields under a `view:<id>` config scope (independent of the base
  // entity's own /data layout). The base entity is still the URL slug.
  const configScope = entity;
  const [hidePii, setHidePii] = useState<boolean>(false);
  // Optional "+" add-field columns — persisted per scope. Drives the
  // `?fields=` query param so the server resolves the extra joined columns.
  const [extraFields, setExtraFields] = useState<string[]>(() =>
    loadExtraFields(configScope),
  );
  // Header-click sort override (`?order=<key>`). `undefined` = the page's
  // default order (the `orderBy` prop — `registration` for /members). Toggling a
  // sortable header sets this and resets pagination; unlike an entity switch it
  // never wipes the search / date filters.
  const [sortOverride, setSortOverride] = useState<string | undefined>(
    undefined,
  );
  const effectiveOrder = sortOverride ?? orderBy;
  /** Refetch counter — bumped after a successful row edit to reuse cursor. */
  const [refetchTick, setRefetchTick] = useState<number>(0);
  /** Currently-edited row (phase 14). null = modal closed. */
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(
    null,
  );
  /** Per-row delete target (opens the dependency-aware delete modal). */
  const [deleteTarget, setDeleteTarget] = useState<Record<
    string,
    unknown
  > | null>(null);
  /**
   * Multi-select delete (2026-07-28). `null` = selection mode off; a Set (even
   * an empty one) = on. Cleared whenever the entity or the result set changes,
   * so an id picked under one filter can never be deleted under another.
   */
  const [selection, setSelection] = useState<ReadonlySet<string> | null>(null);
  /** Pending bulk/whole-table delete awaiting confirmation. */
  const [bulkDelete, setBulkDelete] = useState<
    { readonly kind: "selected"; readonly ids: ReadonlyArray<string> }
    | { readonly kind: "all" }
    | null
  >(null);
  /** Reset-modal open state. */
  const [resetModalOpen, setResetModalOpen] = useState<boolean>(false);
  /** Last reset result shown as a transient banner above the table. */
  const [resetResult, setResetResult] = useState<DevResetResponse | null>(
    null,
  );
  /** Purge-members modal open state (button rendered only under the build flag). */
  const [purgeModalOpen, setPurgeModalOpen] = useState<boolean>(false);
  /** Last purge result shown as a transient banner above the table. */
  const [purgeResult, setPurgeResult] = useState<MemberPurgeResponse | null>(
    null,
  );
  /** Per-entity row counts for the sidebar badges. */
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  // Realtime push signals (migration 041) — drive targeted refetches in place
  // of unconditional polling.
  const rt = useRealtime();
  // Page rows are cached in the MODULE-LEVEL `dataPageCache` (declared above
  // `Data`), keyed by the full query signature. Returning to a previously-
  // viewed tab renders its last result INSTANTLY from there, then revalidates
  // in the background — and because the cache lives at module scope it now
  // survives leaving `/data` entirely (the per-component `useRef` it replaced
  // was dropped on unmount, forcing a full reload on return). Session
  // isolation is preserved by the `registerClearListener` wired at module
  // load: sign-out / stale-session `clearCache()` wipes it.
  // Free-text search (`?q=`) + date-range filter. `searchInput` is the raw
  // keystroke value; `search` is debounced (300ms) and drives the server
  // query so each keystroke doesn't fire a request. All three are
  // server-side filters (find matches across every page, not a client-side
  // hide of the loaded page).
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const extraFieldsKey = extraFields.slice().sort().join(",");
  /**
   * Identity of the CURRENT query — everything that changes which rows the
   * server would return, and deliberately NOTHING that merely re-fetches the
   * same query (`refetchTick`, the 30s safety poll, a Realtime signal). The
   * feed below resets under a new generation exactly when this changes.
   *
   * Keying the reset on this rather than on the response OBJECT is what makes
   * continuous scrolling possible: the response is replaced on every
   * background refresh, and a reset keyed on it snapped the list back to the
   * first 25 rows roughly every 30 seconds. (2026-07-29)
   */
  const queryIdentity = browseQueryIdentity({
    entity,
    configScope,
    nullFiltersKey,
    extraFieldsKey,
    search,
    dateFrom,
    dateTo,
    order: effectiveOrder ?? "",
  });
  /**
   * The `?` params for a browse request. ONE builder for the first page and
   * every scrolled-in batch, so a batch can never come from a different
   * filter/sort than the page it is appended to.
   */
  const buildExtraParams = (): Record<string, string> | undefined => {
    const p: Record<string, string> = {};
    if (hasNullFilter) Object.assign(p, nullFilters);
    if (extraFields.length > 0) p["fields"] = extraFields.join(",");
    if (search.length > 0) p["q"] = search;
    if (dateFrom.length > 0) p["date_from"] = dateFrom;
    if (dateTo.length > 0) p["date_to"] = dateTo;
    if (effectiveOrder !== undefined) p["order"] = effectiveOrder;
    return Object.keys(p).length > 0 ? p : undefined;
  };

  /**
   * The table's rows — the first page plus everything scrolled in below it —
   * through the ONE keyset-feed implementation the Home audit card and the
   * `/agents` panels also use (`useCursorFeed`). The browser's own concerns
   * are all in the options: the query identity above as the generation key;
   * the module-level page cache as an instant, never-armed SEED; `id` as the
   * de-duplication key (rows of the id-less tables are never de-duplicated);
   * and a failed background refresh SURFACED as the "Error:" panel, as it
   * always was here. A non-table view (`discount_codes`) owns its own fetch,
   * so the feed stands down for it — `/api/data/<slug>` would 404.
   *
   * What this replaced (2026-09-08): a cursor synchronised from whatever
   * response was on screen, which armed the OLD query's cursor for a moment
   * after a search was typed and appended its next batch under the new
   * query's params — the one member matching the search rendered twice.
   */
  const feed = useCursorFeed<DataResponse>({
    identity: queryIdentity,
    enabled: !isNonTableView(entity),
    fetchPage: (cursor) => {
      const key = queryIdentity;
      return api
        .data(entity, cursor, PAGE_LIMIT, buildExtraParams())
        .then((r) => {
          if (cursor === null) rememberPage(key, r);
          return r;
        });
    },
    seed: (key) => dataPageCache.get(key) ?? null,
    rowKey: (row) => {
      const id = row["id"];
      return typeof id === "string" ? id : undefined;
    },
    surfaceRefreshFailures: true,
  });
  /**
   * The first page on screen: a cache seed, the live page, or — while a new
   * query's first page is in flight — the previous query's page, so typing a
   * search never flashes "Loading…" between keystrokes.
   */
  const response = feed.first;

  // Reset every per-entity bit when the URL slug changes. PII
  // visibility resets to the default (shown). Phase 38 — also
  // reset when the null-filter set changes (orphan-only toggle
  // shouldn't preserve cursor history from the un-filtered view).
  useEffect(() => {
    setHidePii(false);
    // Reset any header-sort override so a new entity opens at its own default
    // order (the /members page re-seeds `registration` via the orderBy prop).
    setSortOverride(undefined);
    // The previous table's page must not paint under the new title (its
    // columns differ); a cached page for the new table — the feed's seed —
    // stays. The feed's own reset for the new identity has already run: its
    // effect is declared above this one.
    feed.discardStale();
    setEditingRow(null);
    setExtraFields(loadExtraFields(configScope));
    setSearchInput("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  }, [entity, nullFiltersKey, configScope, orderBy, feed.discardStale]);

  // Persist the optional add-field selection per scope.
  useEffect(() => {
    saveExtraFields(configScope, extraFields);
  }, [configScope, extraFields]);

  // Any selection was made against the previous result set.
  useEffect(() => {
    setSelection((prev) => (prev === null ? null : new Set<string>()));
  }, [queryIdentity]);

  // Background refreshes — a Realtime signal, the 30s safety poll, tab focus,
  // a post-edit refetch — re-read the first page IN PLACE. The scrolled-in
  // tail and its cursor are untouched, so the list never snaps back; anything
  // that changes WHICH rows exist (a delete, a reset) calls `feed.dropTail()`
  // first, or a deleted row would linger below the fold.
  useEffect(() => {
    if (refetchTick === 0) return;
    void feed.refresh();
  }, [refetchTick, feed.refresh]);

  // Migration 041 — Realtime push replaces the old 3s polling. The table
  // refetches when a change signal names the active entity (or, for the
  // members tab, a table feeding its computed columns). A slow safety-net
  // poll + refetch-on-reconnect / tab-focus guarantees eventual consistency
  // if the socket drops. Paused while a row-edit modal is open.
  useEffect(() => {
    if (rt.signalSeq === 0) return;
    if (editingRow !== null) return;
    const changed = rt.changedEntities;
    const relevant =
      changed.includes(entity) ||
      (entity === "members" &&
        changed.some((c) => MEMBERS_FEEDER_ENTITIES.has(c)));
    if (relevant) setRefetchTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt.signalSeq]);

  // Safety-net poll (was 3s unconditional; now 30s backstop only).
  useEffect(() => {
    if (editingRow !== null) return undefined;
    const id = setInterval(() => {
      setRefetchTick((t) => t + 1);
    }, SAFETY_POLL_MS);
    return () => {
      clearInterval(id);
    };
  }, [editingRow]);

  // Refetch once when the tab regains focus or the Realtime channel
  // (re)connects — covers anything missed while hidden / disconnected.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        setRefetchTick((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  useEffect(() => {
    if (rt.connected) setRefetchTick((t) => t + 1);
  }, [rt.connected]);

  // Per-entity row counts for the sidebar badges. Fetched on mount, on every
  // realtime signal (counts are ~1ms), and on the safety-net refetch tick.
  // Paused while a row-edit modal is open. Skipped entirely when the sidebar
  // is hidden (the standalone /members page): nothing renders the counts
  // there, and `/api/data-counts` belongs to the Data-browser page — for a
  // viewer granted Members but not Data browser the fetch would 403 on every
  // mount, logging a page_access_denied that reads as a break-in attempt.
  useEffect(() => {
    if (!showSidebar) return undefined;
    if (editingRow !== null) return undefined;
    let cancelled = false;
    void api
      .dataCounts()
      .then((r) => {
        if (!cancelled) setCounts(r.counts);
      })
      .catch(() => {
        // Silently ignore — counts are decorative; table state is
        // authoritative for errors. Stale counts persist on failure.
      });
    return () => {
      cancelled = true;
    };
  }, [showSidebar, editingRow, refetchTick, rt.signalSeq]);

  // Toggle a sortable header caret: clicking the active-DESC column flips it to
  // ASC; any other click sorts that column DESC. A new order is a new keyset
  // space, so it changes `queryIdentity` and the scroll buffer restarts from
  // the top; search / filters are preserved. `descKey` / `ascKey` are the
  // server `?order=` keys carried in the payload's `sortable_columns`.
  const onSort = (descKey: string, ascKey: string): void => {
    setSortOverride((cur) => {
      const active = cur ?? orderBy;
      return active === descKey ? ascKey : descKey;
    });
  };

  // Phase 38 — `ENTITY_UPDATE_ACTIONS` admits string OR string[].
  // Single-string entries (members → 'update_member') keep their
  // single-modal UX; array entries (payments → ['reconcile_orphan_payment',
  // 'unlink_payment']) render a multi-action modal with per-row
  // conditional surfacing (e.g. unlink only when member_id is non-null).
  const updateActionRaw = ENTITY_UPDATE_ACTIONS[entity];
  const updateActionNames: ReadonlyArray<string> =
    updateActionRaw === undefined
      ? []
      : Array.isArray(updateActionRaw)
        ? updateActionRaw
        : [updateActionRaw];
  const updateAction =
    updateActionNames.length === 1 ? updateActionNames[0] : undefined;
  const hasRowActions = updateActionNames.length > 0;
  // A row click NAVIGATES when `rowClickTo` is set (the standalone /members
  // page → the member profile), otherwise it opens the in-place edit modal
  // (the /data browser). Either makes the row clickable.
  const rowsClickable = rowClickTo !== undefined || hasRowActions;
  const onRowClick = (row: Record<string, unknown>): void => {
    if (rowClickTo !== undefined) {
      navigate(rowClickTo(row));
      return;
    }
    if (!hasRowActions) return;
    setEditingRow(row);
  };

  // Phase 38 — orphan-payment banner. Surfaces N orphan payments
  // when `/data/payments?member_id=null` is active. The count is
  // approximate (paginated — N reflects current page; the user can
  // page through to see all). Provides a quick "click any row →
  // Reconcile" call-to-action.
  const showOrphanBanner =
    entity === "payments" &&
    nullFilters["member_id"] === "null" &&
    response !== null;

  // "Add member" (members entity only). On the mobile Members tab (`declutter`)
  // it renders as a compact "+" icon that shares the search row; on desktop it
  // keeps its full label and heads the single control row.
  const addMemberButton =
    entity === "members" ? (
      <ActionModalButton
        action="staff_create_member"
        buttonLabel={declutter ? "+" : "Add Member"}
        buttonClassName={`data-add-member-btn${
          declutter ? " data-add-member-btn--icon" : ""
        }`}
        {...(declutter ? { buttonTitle: "Add a member" } : {})}
        modalTitle="Add a member"
        submitLabel="Create member"
        fields={[
          {
            kind: "text",
            name: "email",
            label: "Email",
            required: true,
            placeholder: "name@example.com",
          },
          {
            kind: "text",
            name: "display_name",
            label: "Name",
            placeholder: "Full name",
          },
          {
            kind: "text",
            name: "phone",
            label: "Phone",
            placeholder: "+447700900000 (E.164)",
          },
          // Everything below writes into `members.metadata` — the same keys the
          // profile's own editors patch, so a member created here opens with a
          // filled-in profile instead of one that has to be edited immediately.
          {
            kind: "text",
            name: "metadata.instagram_handle",
            label: "Instagram",
            placeholder: "handle, without the @",
          },
          {
            kind: "select",
            name: "metadata.gender",
            label: "Gender",
            options: [
              { value: "", label: "—" },
              ...MEMBER_GENDER_OPTIONS.map((g) => ({ value: g, label: g })),
            ],
          },
          {
            kind: "select",
            name: "status",
            label: "Status",
            defaultValue: "active",
            options: [
              { value: "active", label: "Active" },
              { value: "suspended", label: "Suspended" },
            ],
          },
          // The three preference flags default to what the profile treats as
          // unset: contact channels and post-session messages are ON unless
          // turned off, feedback requests are OFF unless opted in.
          {
            kind: "checkbox",
            name: "metadata.email_opt_in",
            label: "Contact via email",
            defaultValue: true,
          },
          {
            kind: "checkbox",
            name: "metadata.whatsapp_opt_in",
            label: "Contact via WhatsApp",
            defaultValue: true,
          },
          {
            kind: "checkbox",
            name: "metadata.send_post_session_message",
            label: "Send post-session messages",
            defaultValue: true,
          },
          {
            kind: "checkbox",
            name: "metadata.session_feedback_opt_in",
            label: "Ask for session feedback",
            defaultValue: false,
          },
        ]}
        onDone={() => setRefetchTick((t) => t + 1)}
      />
    ) : null;

  return (
    <div className="data-browser">
      <h1 className="page-title">{title}</h1>
      {resetResult !== null ? (
        <div className="empty" data-reset-banner>
          <strong>Reset complete.</strong> Emptied{" "}
          <code>{resetResult.tables_purged}</code> tables; deleted{" "}
          <code>{resetResult.members_deleted}</code> member
          {resetResult.members_deleted === 1 ? "" : "s"} and{" "}
          <code>{resetResult.pa_actions_deleted}</code> retired PA action
          {resetResult.pa_actions_deleted === 1 ? "" : "s"}; kept{" "}
          <code>{resetResult.peter_actions_kept}</code> of Peter&rsquo;s and{" "}
          <code>{resetResult.inbox_actions_kept}</code> of the inbox
          agent&rsquo;s; removed{" "}
          <code>{resetResult.inbox_threads_deleted}</code> member-owned inbox
          thread{resetResult.inbox_threads_deleted === 1 ? "" : "s"}. Studio,
          trainers, config + the PR and operator-inbox pipelines preserved.
        </div>
      ) : null}
      {purgeResult !== null ? (
        <div className="empty" data-purge-banner>
          <strong>Members purged.</strong> Deleted{" "}
          <code>{purgeResult.members_deleted}</code> member
          {purgeResult.members_deleted === 1 ? "" : "s"},{" "}
          <code>
            {Object.values(purgeResult.rows_deleted).reduce((a, b) => a + b, 0)}
          </code>{" "}
          member-owned rows and{" "}
          <code>{purgeResult.messages_deleted}</code> message
          {purgeResult.messages_deleted === 1 ? "" : "s"} (kept{" "}
          <code>{purgeResult.pr_messages_kept}</code> pipeline-counterparty
          message{purgeResult.pr_messages_kept === 1 ? "" : "s"}); removed{" "}
          <code>{purgeResult.inbox_threads_deleted}</code> member-owned inbox
          thread{purgeResult.inbox_threads_deleted === 1 ? "" : "s"}; unassigned{" "}
          <code>{purgeResult.tasks_unassigned}</code> task
          {purgeResult.tasks_unassigned === 1 ? "" : "s"}; healed{" "}
          <code>{purgeResult.slots_healed}</code> slot
          {purgeResult.slots_healed === 1 ? "" : "s"}. Studio, trainers, slots,
          config, keys and the PR + operator-inbox pipelines preserved.
        </div>
      ) : null}
      {showOrphanBanner ? (
        <div className="empty" data-orphan-payments-banner>
          <strong>
            {response?.count_returned ?? 0} orphan payment
            {(response?.count_returned ?? 0) === 1 ? "" : "s"}
            {response?.next_cursor !== null ? "+" : ""}
          </strong>
          {" "}on this page. Click any row → Reconcile (link to a member)
          to clear the queue.
        </div>
      ) : null}
      <div
        className={`data-browser-grid${
          showSidebar ? "" : " data-browser-grid--full"
        }`}
      >
        {showSidebar ? (
        <aside className="data-browser-sidebar">
          <div className="data-browser-sidebar-head">
            <h2 className="data-browser-sidebar-title">Entity</h2>
            <div className="data-browser-sidebar-actions">
              {MEMBER_PURGE_BUTTON_VISIBLE ? (
                <button
                  type="button"
                  className="data-browser-reset-btn"
                  onClick={() => setPurgeModalOpen(true)}
                  title="Purge members: hard-delete every member of THIS studio and everything they own — bookings, payments, credits, conversations, WhatsApp / email / chat messages, notes, memory. Keeps the studio, trainers, slots, config, secrets, keys and the PR + operator-inbox pipelines."
                >
                  Purge members
                </button>
              ) : null}
              <button
                type="button"
                className="data-browser-reset-btn"
                onClick={() => setResetModalOpen(true)}
                title="Reset to the E-Pulsive baseline: hard-delete members, their history and the retired PA stack. Keeps studio, trainers, config, the whole PR pipeline and the whole operator inbox. Dev only."
              >
                Reset DB
              </button>
            </div>
          </div>
          {/* C1 (2026-07-29) — grouped, searchable rail. Mario preferred the
              /config console's shape to this page's flat list of 34 raw
              snake_case table names, which was a schema browser rather than a
              business one. Grouping + labels come from the SERVER alongside
              the entity list, so there is no second copy here to drift. */}
          <input
            type="search"
            className="data-browser-entity-search"
            placeholder="Search entities…"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            aria-label="Filter entities"
          />
          {entityGroups.length === 0 ? (
            <p className="data-browser-views-empty">No entity matches.</p>
          ) : null}
          {entityGroups.map((group) => (
            <div className="data-browser-entity-group" key={group.name}>
              <div className="data-browser-entity-group-label">
                {group.name}
              </div>
              <ul className="data-browser-entity-list">
                {group.entities.map((e) => {
                  const n = counts === null ? null : (counts[e] ?? null);
                  return (
                    <li key={e}>
                      <NavLink
                        to={`/data/${e}`}
                        className={({ isActive }) =>
                          isActive
                            ? "data-browser-entity-link active"
                            : "data-browser-entity-link"
                        }
                        onClick={() => {
                          // Resetting handled by the useEffect that watches
                          // the URL param, but seed the navigate so the
                          // route is the entity's URL.
                          navigate(`/data/${e}`);
                        }}
                      >
                        {/* Label only. The raw table name used to sit beside
                            it in grey, but for most entities the two are the
                            same word ("Studios studios") and the duplicate was
                            taking the width the label needed — which is why
                            "Conversations" and "Member packages" rendered as
                            "Conversati…" and "Packag…". The table name is
                            still one glance away, in the URL. */}
                        <span className="data-browser-entity-names">
                          <span className="data-browser-entity-label">
                            {labels[e] ?? e}
                          </span>
                        </span>
                        {n !== null ? (
                          <span className="data-browser-entity-count">{n}</span>
                        ) : null}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>
        ) : null}
        <section
          className={`data-browser-content${
            compactTools ? " data-browser-content--onerow" : ""
          }`}
        >
          {isNonTableView(entity) ? (
            // Not a table — a Stripe-backed view that owns its own fetch.
            <DiscountCodesPanel />
          ) : feed.error !== null ? (
            <p className="empty">Error: {feed.error}</p>
          ) : response === null ? (
            <p className="empty">Loading…</p>
          ) : (
            <>
              {addMemberButton !== null && !declutter && !compactTools ? (
                <div className="data-members-toolbar">{addMemberButton}</div>
              ) : null}
              {response.searchable || response.date_filterable ? (
                <DataFilterBar
                  searchable={response.searchable}
                  dateFilterable={response.date_filterable && !declutter}
                  searchInput={searchInput}
                  onSearchChange={setSearchInput}
                  dateFrom={dateFrom}
                  onDateFromChange={setDateFrom}
                  dateTo={dateTo}
                  onDateToChange={setDateTo}
                  onClear={() => {
                    setSearchInput("");
                    setSearch("");
                    setDateFrom("");
                    setDateTo("");
                  }}
                  {...(declutter && addMemberButton !== null
                    ? { trailing: addMemberButton }
                    : {})}
                />
              ) : null}
              {declutter && entity === "members" ? (
                /* The phone Members tab swaps the wide table for compact
                   cards — no horizontal scrolling, whole card visible. The
                   filter bar above (search + the "+" Add-member button) and
                   the row-click navigation are shared with the table path.
                   `onEdit` / `onDelete` are the card double-tap's quick
                   actions; they raise the SAME two modals the table's row
                   click and per-row Delete button do, rendered by this host
                   below — `update_member` is the members entry in
                   ENTITY_UPDATE_ACTIONS, so `setEditingRow` reaches
                   UpdateMemberModal here exactly as it does on /data/members. */
                <MemberCardList
                  rows={feed.rows}
                  onOpen={onRowClick}
                  onEdit={(row) => setEditingRow(row)}
                  onDelete={(row) => setDeleteTarget(row)}
                  onLoadMore={feed.loadMore}
                  hasMore={feed.hasMore}
                  loadingMore={feed.loadingMore}
                />
              ) : (
              <DataTable
              response={response}
              declutter={declutter}
              hidePii={hidePii}
              onToggleHide={() => setHidePii((v) => !v)}
              activeOrder={effectiveOrder}
              onSort={onSort}
              onRowClick={rowsClickable ? onRowClick : null}
              entity={entity}
              onDeleteRow={(row) => setDeleteTarget(row)}
              extraRows={feed.tailRows}
              onLoadMore={feed.loadMore}
              hasMore={feed.hasMore}
              loadingMore={feed.loadingMore}
              selection={selection}
              onStartSelection={() => setSelection(new Set<string>())}
              onCancelSelection={() => setSelection(null)}
              onToggleSelect={(id) =>
                setSelection((prev) => {
                  const next = new Set(prev ?? []);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              onDeleteSelected={() =>
                setBulkDelete({ kind: "selected", ids: [...(selection ?? [])] })
              }
              onDeleteAll={() => setBulkDelete({ kind: "all" })}
              canDeleteAll={!DELETE_ALL_PROTECTED_UI.has(entity)}
              configKey={configScope}
              activeFields={extraFields}
              onToggleField={(key) =>
                setExtraFields((prev) =>
                  prev.includes(key)
                    ? prev.filter((k) => k !== key)
                    : [...prev, key],
                )
              }
              compactTools={compactTools}
              {...(compactTools && addMemberButton !== null && !declutter
                ? { leading: addMemberButton }
                : {})}
              />
              )}
            </>
          )}
        </section>
      </div>
      {editingRow !== null && updateAction === "update_member" ? (
        <UpdateMemberModal
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSuccess={() => {
            setEditingRow(null);
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
      {editingRow !== null && entity === "payments" ? (
        <PaymentReconciliationModal
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSuccess={() => {
            setEditingRow(null);
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
      {deleteTarget !== null ? (
        <DeleteRowModal
          entity={entity}
          row={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => {
            setDeleteTarget(null);
            feed.dropTail();
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
      {bulkDelete !== null ? (
        <BulkDeleteModal
          entity={entity}
          target={bulkDelete}
          onClose={() => setBulkDelete(null)}
          onSuccess={() => {
            setBulkDelete(null);
            setSelection(null);
            clearCache();
            feed.dropTail();
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
      {resetModalOpen ? (
        <ResetDbModal
          onClose={() => setResetModalOpen(false)}
          onSuccess={(result) => {
            setResetModalOpen(false);
            setResetResult(result);
            setEditingRow(null);
            feed.dropTail();
            // A dev-reset truncates every table — drop ALL cached pages
            // (this module's `dataPageCache` via the clear listener + the
            // global store) so no stale pre-reset rows can seed a view.
            clearCache();
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
      {purgeModalOpen ? (
        <PurgeMembersModal
          onClose={() => setPurgeModalOpen(false)}
          onSuccess={(result) => {
            setPurgeModalOpen(false);
            setPurgeResult(result);
            setEditingRow(null);
            feed.dropTail();
            // Same reasoning as the reset: every member-owned entity page in
            // the cache is now stale, so drop them all rather than guess.
            clearCache();
            setRefetchTick((t) => t + 1);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataFilterBar — free-text search + date-range, shown below the heading for
// entities that support them (`response.searchable` / `date_filterable`).
// Server-side filters: typing finds matches across every page (not a
// client-side hide of the loaded rows). The search box matches any member-
// identifying column (id / display_name / email / phone for members; the
// resolved member name/email for bookings & payments; sender + body for
// message tables) — so "spe" surfaces spencer.…@… without typing the rest.
// ---------------------------------------------------------------------------

interface DataFilterBarProps {
  readonly searchable: boolean;
  readonly dateFilterable: boolean;
  readonly searchInput: string;
  readonly onSearchChange: (v: string) => void;
  readonly dateFrom: string;
  readonly onDateFromChange: (v: string) => void;
  readonly dateTo: string;
  readonly onDateToChange: (v: string) => void;
  readonly onClear: () => void;
  /**
   * Optional element rendered flush-right in the same row as the search box —
   * the mobile Members tab passes its compact "+" Add-member button here so it
   * shares the search row instead of sitting on its own line above.
   */
  readonly trailing?: ReactNode;
}

function DataFilterBar({
  searchable,
  dateFilterable,
  searchInput,
  onSearchChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  onClear,
  trailing,
}: DataFilterBarProps): JSX.Element {
  const hasActive =
    searchInput.length > 0 || dateFrom.length > 0 || dateTo.length > 0;
  return (
    <div
      className={`data-filter-bar${
        trailing !== undefined ? " data-filter-bar--compact" : ""
      }`}
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {searchable ? (
        <label className="data-filter-search">
          <span className="data-filter-label">Search</span>
          <input
            type="search"
            value={searchInput}
            placeholder="id · name · email · phone…"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </label>
      ) : null}
      {trailing !== undefined ? (
        <div className="data-filter-trailing">{trailing}</div>
      ) : null}
      {dateFilterable ? (
        <div className="data-filter-dates">
          <label className="data-filter-date">
            <span className="data-filter-label">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
            />
          </label>
          <label className="data-filter-date">
            <span className="data-filter-label">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
            />
          </label>
        </div>
      ) : null}
      {hasActive ? (
        <button
          type="button"
          className="data-browser-pager-btn"
          onClick={onClear}
          title="Clear search + date filters"
        >
          Clear
        </button>
      ) : null}
    </div>
  );
}

interface DataTableProps {
  readonly response: DataResponse;
  /**
   * Mobile quick-access declutter (the phone Members tab). Hides the whole
   * table-chrome toolbar + the drag hint, and forces additional columns hidden
   * for this render (the persisted `hideAdditional` state is left untouched, so
   * desktop is unaffected).
   */
  readonly declutter?: boolean;
  readonly hidePii: boolean;
  readonly onToggleHide: () => void;
  /** Active `?order=` key — drives which header sort caret reads as active. */
  readonly activeOrder?: string | undefined;
  /**
   * Toggle a sortable header between its DESC and ASC `?order=` keys. When
   * absent (or the header isn't in `response.sortable_columns`) no caret shows.
   */
  readonly onSort?: (descKey: string, ascKey: string) => void;
  /** Phase 14 — when defined, every row is clickable. */
  readonly onRowClick: ((row: Record<string, unknown>) => void) | null;
  /** The browsable entity (table) name — drives the per-row delete endpoint. */
  readonly entity: string;
  /** Open the dependency-aware delete modal for a row (far-right column). */
  readonly onDeleteRow: ((row: Record<string, unknown>) => void) | null;
  /**
   * localStorage namespace for the column layout (order/widths/hidden/...).
   * Defaults to the entity name; saved views pass `view:<id>` so each view's
   * layout is independent of the base entity's own /data layout.
   */
  readonly configKey: string;
  /** Optional add-field keys currently active (the "+" menu selection). */
  readonly activeFields: ReadonlyArray<string>;
  /** Toggle an optional add-field column on/off. */
  readonly onToggleField: (key: string) => void;
  /**
   * Collapse the whole toolbar — caption, row-height, the two visibility
   * toggles and the four buttons — into ONE "+" menu, and drop the caption
   * entirely. The standalone `/members` page passes true; `/data/:entity`
   * keeps the full strip, where the caption (row count, ordering, PII count)
   * earns its space for a power user browsing arbitrary tables.
   */
  readonly compactTools?: boolean;
  /**
   * Rendered at the head of the compact toolbar, before the "+" — the Members
   * page passes its "Add Member" button so the two share one control row with
   * the search + date filters (see `.data-browser-content--onerow`).
   */
  readonly leading?: ReactNode;
  // --- 2026-07-28: infinite scroll + multi-select delete -------------------
  /**
   * Rows fetched by scrolling PAST the current page, appended below
   * `response.rows`. Kept separate from `response` so Prev/Next still operate
   * on whole pages and the page cache stays a cache of pages.
   */
  readonly extraRows: ReadonlyArray<Record<string, unknown>>;
  /** Fetch the next 25 and append. No-op while one is already in flight. */
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  /** Selected row ids, or null when selection mode is off. */
  readonly selection: ReadonlySet<string> | null;
  readonly onStartSelection: () => void;
  readonly onCancelSelection: () => void;
  readonly onToggleSelect: (id: string) => void;
  readonly onDeleteSelected: () => void;
  readonly onDeleteAll: () => void;
  /** False for tables holding the studio's identity/config — see DELETE_ALL_PROTECTED. */
  readonly canDeleteAll: boolean;
}

// ---------------------------------------------------------------------------
// Per-entity table layout: column order, widths, "additional" set, row
// height, and the hide-additional toggle. Persisted in localStorage so a
// staffer's arrangement survives navigation + refresh.
// ---------------------------------------------------------------------------

const LS_PREFIX = "layer:data-table:";
const DEFAULT_COL_WIDTH = 180;
/** Fixed width of the far-right per-row Delete actions column. */
const ACTIONS_COL_WIDTH = 84;
const MIN_COL_WIDTH = 60;
const DRAG_THRESHOLD = 4;
const ROW_HEIGHTS: ReadonlyArray<{ label: string; px: number }> = [
  { label: "Compact", px: 30 },
  { label: "Normal", px: 0 },
  { label: "Tall", px: 60 },
];

interface TableConfig {
  order: string[];
  widths: Record<string, number>;
  additional: string[];
  /** Columns explicitly hidden via the "Columns" picker. */
  hidden: string[];
  rowHeight: number;
  hideAdditional: boolean;
}

// Per-entity DEFAULT column layout. Applied only when the staffer has NO saved
// layout for the entity (and after "Reset layout") — a saved layout always
// wins. Lets an entity open with a curated column order + a default
// "additional" (hidden-under-"Hide additional") set instead of the raw
// table-column order. `resend_message_events` opens like Resend's own email
// log — To (recipient) · Status (event_type) · Subject · Sent (received_at) —
// with the ids, vendor refs, and the created_at/studio_id plumbing tucked into
// "additional". Keyed by base entity name.
const DEFAULT_LAYOUT: Record<
  string,
  {
    order: string[];
    additional: string[];
    hideAdditional?: boolean;
    /**
     * Bump when this entity's default order changes. A saved layout normally
     * wins forever, which means anyone who has ever opened the table would
     * never see a new default — the layout is written on first render, so that
     * is everyone. The version is folded into the localStorage KEY, so a bump
     * orphans the old entry and the table opens on the new default once. A
     * staffer's own re-ordering after that is saved under the new key and
     * survives normally.
     */
    version?: number;
  }
> = {
  // Members opens on who the person is, then what they are worth: identity
  // (email · phone · name · status · joined) followed by the four computed
  // columns that answer "should I care about this person today". The plumbing
  // — studio_id, consent, metadata, updated_at, the Codex registration number
  // — is marked additional and collapsed, so it is one toggle away rather than
  // padding the width. The four computed keys only appear once
  // DEFAULT_EXTRA_FIELDS has switched them on; naming them here is what fixes
  // their POSITION when they do.
  members: {
    order: [
      "id",
      "email",
      "phone",
      "display_name",
      "status",
      "created_at",
      "available_credits",
      "next_session_at",
      "lifetime_spent_minor",
      "total_bookings",
      "instagram_handle",
      "studio_id",
      "consent",
      "metadata",
      "updated_at",
      "codex_registration_no",
    ],
    additional: [
      "instagram_handle",
      "studio_id",
      "consent",
      "metadata",
      "updated_at",
      "codex_registration_no",
    ],
    hideAdditional: true,
    version: 1,
  },
  resend_message_events: {
    order: [
      "recipient_identifier",
      "event_type",
      "subject",
      "received_at",
      "vendor_message_id",
      "vendor_event_id",
      "id",
      "created_at",
      "studio_id",
    ],
    additional: [
      "vendor_message_id",
      "vendor_event_id",
      "id",
      "created_at",
      "studio_id",
    ],
    // Open with the additional columns collapsed so the table shows exactly
    // To · Status · Subject · Sent (like Resend) until the staffer expands.
    hideAdditional: true,
  },
};

/**
 * Storage suffix for an entity whose default layout is versioned. A saved
 * view (`view:<id>`) carries its own scope and is never versioned — the
 * lookup falls through to `undefined` and the suffix is empty.
 */
function layoutVersionSuffix(scope: string): string {
  const v = DEFAULT_LAYOUT[scope]?.version;
  return v === undefined ? "" : `:v${v}`;
}

function loadConfig(entity: string): Partial<TableConfig> {
  try {
    const raw = localStorage.getItem(
      LS_PREFIX + entity + layoutVersionSuffix(entity),
    );
    return raw ? (JSON.parse(raw) as Partial<TableConfig>) : {};
  } catch {
    return {};
  }
}

const LS_FIELDS_PREFIX = "layer:data-fields:";

/**
 * Computed columns an entity opens with when the staffer has never chosen for
 * themselves. A SAVED selection always wins — including a saved EMPTY one, so
 * switching these off stays switched off. `DEFAULT_LAYOUT` fixes where they
 * sit; this decides that they are on at all.
 */
const DEFAULT_EXTRA_FIELDS: Record<string, ReadonlyArray<string>> = {
  members: [
    "available_credits",
    "next_session_at",
    "lifetime_spent_minor",
    "total_bookings",
  ],
};

/** Persisted optional add-field selection for an entity ("+" menu). */
function loadExtraFields(entity: string): string[] {
  try {
    const raw = localStorage.getItem(
      LS_FIELDS_PREFIX + entity + layoutVersionSuffix(entity),
    );
    // No stored key at all = never chosen, so fall back to the entity default.
    // An empty ARRAY is a real choice ("show none") and is honoured. The
    // version suffix means a defaults bump reads as "never chosen" once.
    if (!raw) return [...(DEFAULT_EXTRA_FIELDS[entity] ?? [])];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === "string")
      : [];
  } catch {
    return [...(DEFAULT_EXTRA_FIELDS[entity] ?? [])];
  }
}

function saveExtraFields(scope: string, fields: ReadonlyArray<string>): void {
  try {
    localStorage.setItem(
      LS_FIELDS_PREFIX + scope + layoutVersionSuffix(scope),
      JSON.stringify(fields),
    );
  } catch {
    // localStorage may be unavailable (private mode) — non-fatal.
  }
}

function arraysEqual(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

interface DragView {
  /** column being dragged (visible-index) — its in-table cell is dimmed */
  readonly fromVis: number;
  /** the dragged column name (shown in the floating ghost) */
  readonly col: string;
  /** boundary index where the drop line is drawn (0..n) */
  readonly insertIndex: number;
  /** x of the drop line in table-content coordinates (px) */
  readonly lineX: number;
  /** live cursor position (viewport coords) for the floating ghost */
  readonly pointerX: number;
  readonly pointerY: number;
}

interface DragGeom {
  readonly col: string;
  readonly fromVis: number;
  readonly startX: number;
  /** cumulative left edge per boundary (length n+1) */
  readonly boundaries: number[];
  readonly visible: string[];
  readonly scrollEl: HTMLDivElement | null;
  active: boolean;
}

/**
 * Minor-units → "£1,234" / "£57.60" for the mobile member cards. Local on
 * purpose: the members LIST payload carries no currency column (the detail
 * page's formatter needs one), and every E-Pulsive payment is GBP — so the
 * card formats sterling and drops the pennies when they are zero, because at
 * card size "£1,240" reads and "£1,240.00" pads.
 */
function fmtCardPoundsMinor(v: unknown): string {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  if (!Number.isFinite(n)) return "£0";
  const pounds = n / 100;
  return pounds.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: Number.isInteger(pounds) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** A row value as trimmed display text, "" when absent. */
function memberCardText(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (v === null || v === undefined) return "";
  return String(v);
}

/**
 * The `next_session_at` computed field as a compact card date ("2 Sep"), ""
 * when absent or unparseable. Strict `Date` check — a lenient parse of a
 * malformed value must render nothing, never a year-2001 artifact.
 */
function fmtCardNextSession(v: unknown): string {
  if (typeof v !== "string" || v.trim() === "") return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/**
 * How long a single tap waits before it opens the member profile, giving a
 * second tap the chance to arrive and mean something else instead. The
 * `/data/<entity>` table debounces its row click by 200ms for exactly this
 * reason (a double-click there copies a cell); the phone gets a slightly
 * longer window because a thumb is less precise than a mouse.
 */
const MEMBER_CARD_OPEN_DELAY_MS = 220;

/**
 * The phone Members tab — one compact card per member instead of the wide
 * table. Rendered ONLY under `declutter` (mobile `/members`); desktop and the
 * `/data/:entity` browser keep the full DataTable. Each card is the six facts
 * an operator reaches for on the phone, in two columns: the name over one pill
 * per channel identifier (email / phone / Instagram, stacked — a real address
 * and a real E.164 number cannot share 390px without truncating one to
 * uselessness), and a right-aligned column of the three numbers (next session,
 * credits, lifetime spend), one per line and all one colour so they read
 * vertically down the list. The WHOLE card is visible with no horizontal
 * scrolling. The infinite-scroll sentinel + status line mirror the table's
 * continuous-list behaviour, so search and Add-member above it keep working
 * unchanged.
 *
 * TWO gestures, because a phone has no right-click and the wide table's
 * per-row Delete column does not fit:
 *
 * - SINGLE tap opens the member profile (the same `rowClickTo` navigation a
 *   desktop row click performs) — but debounced by
 *   `MEMBER_CARD_OPEN_DELAY_MS`, because a first tap that navigated away
 *   instantly would unmount the list before a second tap could land.
 * - DOUBLE tap reveals an inline Edit / Delete tray under that card. Those
 *   raise the SAME `UpdateMemberModal` / `DeleteRowModal` the desktop browser
 *   uses; the host (`DataInner`) renders them, not this subtree, so closing
 *   the tray as the modal opens cannot take the modal down with it.
 *
 * Touch has no `dblclick` a page can rely on, so the second tap is detected by
 * hand via `isDoubleTap` on `pointerup` — the shared helper `CalendarTimeline`
 * and the Calendar month grid use. `onDoubleClick` covers a mouse, and the
 * `pointerup` path bails on `pointerType === "mouse"` so the two never
 * double-count. A touch double-tap still produces a trailing `click`, which
 * `suppressClick` swallows — otherwise the gesture would open the tray AND
 * schedule a navigation away from it.
 *
 * A row with no string `id` keeps plain tap-to-open and grows no tray: both
 * modals key on `id`, so neither could target it (the same reason the table
 * renders no Delete button for such a row).
 */
function MemberCardList({
  rows,
  onOpen,
  onEdit,
  onDelete,
  onLoadMore,
  hasMore,
  loadingMore,
}: {
  readonly rows: ReadonlyArray<Record<string, unknown>>;
  readonly onOpen: (row: Record<string, unknown>) => void;
  readonly onEdit: (row: Record<string, unknown>) => void;
  readonly onDelete: (row: Record<string, unknown>) => void;
  readonly onLoadMore: () => void;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
}): JSX.Element {
  /** Which card is showing its Edit / Delete tray — at most one at a time. */
  const [openId, setOpenId] = useState<string | null>(null);
  /** Pending debounced profile-open, cancelled by a second tap. */
  const openTimer = useRef<number | null>(null);
  /** First tap of a candidate double-tap (touch only). */
  const tap = useRef<TapCandidate | null>(null);
  /** Swallow the `click` that trails a touch-detected double-tap. */
  const suppressClick = useRef(false);

  const cancelPendingOpen = (): void => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  // Never leave a scheduled navigation behind on unmount — a filter change or
  // a route away mid-debounce would otherwise fire `onOpen` into a dead tree.
  useEffect(
    () => () => {
      if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    },
    [],
  );

  // Escape closes the tray (a keyboard is rare here, but the tray is a
  // transient overlay and every other one in the browser dismisses this way).
  useEffect(() => {
    if (openId === null) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId]);

  // A press anywhere outside the open card dismisses its tray. Matched by the
  // `data-member-row` attribute rather than a ref map, and compared as a
  // STRING so a uuid never has to be escaped into a selector. Registered only
  // while a tray is open, and always after the gesture that opened it, so it
  // cannot close the tray it was installed for.
  useEffect(() => {
    if (openId === null) return undefined;
    const onDown = (e: PointerEvent): void => {
      const t = e.target;
      const li = t instanceof Element ? t.closest("[data-member-row]") : null;
      if (li !== null && li.getAttribute("data-member-row") === openId) return;
      setOpenId(null);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [openId]);

  if (rows.length === 0) {
    return <p className="empty">No members.</p>;
  }
  return (
    <div className="member-card-list-wrap">
      <ul className="member-card-list">
        {rows.map((row, i) => {
          const id = memberCardText(row["id"]);
          // Channel-signup placeholders (<igsid>@instagram.placeholder.layer,
          // <phone>@whatsapp.placeholder.layer) are routing artifacts, not an
          // address anyone can write to — never show one as the member's email.
          const rawEmail = memberCardText(row["email"]);
          const email = isPlaceholderEmail(rawEmail) ? "" : rawEmail;
          const phone = memberCardText(row["phone"]);
          const name = memberCardText(row["display_name"]);
          const insta = memberCardText(row["instagram_handle"]).replace(
            /^@/,
            "",
          );
          const credits = Number(row["available_credits"] ?? 0);
          const nextSession = fmtCardNextSession(row["next_session_at"]);
          // The card's own headline, reused as the accessible name of the
          // tray so a screen reader hears "Edit Jane Doe", not a bare "Edit".
          const label = name !== "" ? name : email !== "" ? email : phone;
          // Both quick actions target the row by `id`; without one there is
          // nothing to edit or delete, so the card keeps tap-to-open only.
          const actionable = id !== "";
          const open = actionable && openId === id;
          const revealActions = (): void => {
            if (!actionable) return;
            cancelPendingOpen();
            setOpenId(id);
          };
          return (
            <li
              key={id !== "" ? id : `row-${i}`}
              {...(actionable ? { "data-member-row": id } : {})}
            >
              <button
                type="button"
                className={`member-card${open ? " member-card-selected" : ""}`}
                title={
                  actionable
                    ? "Tap to open · double-tap for Edit / Delete"
                    : undefined
                }
                onPointerDown={() => {
                  // A fresh gesture: nothing from an abandoned one is pending.
                  // Runs BEFORE this gesture's own `pointerup` sets the flag,
                  // so it can only clear a stale suppression, never a live one.
                  suppressClick.current = false;
                }}
                onPointerUp={(e) => {
                  // Mouse arrives as `onDoubleClick`; counting it here too
                  // would fire the tray twice off one gesture.
                  if (e.pointerType === "mouse" || !actionable) return;
                  const next: TapCandidate = {
                    t: e.timeStamp,
                    x: e.clientX,
                    y: e.clientY,
                    key: id,
                  };
                  if (isDoubleTap(tap.current, next)) {
                    tap.current = null;
                    suppressClick.current = true;
                    revealActions();
                    return;
                  }
                  tap.current = next;
                }}
                onDoubleClick={revealActions}
                onClick={() => {
                  if (suppressClick.current) {
                    suppressClick.current = false;
                    cancelPendingOpen();
                    return;
                  }
                  cancelPendingOpen();
                  // Tapping the card whose tray is open puts it away again,
                  // rather than navigating out from under the operator's thumb.
                  if (open) {
                    setOpenId(null);
                    return;
                  }
                  openTimer.current = window.setTimeout(() => {
                    openTimer.current = null;
                    setOpenId(null);
                    onOpen(row);
                  }, MEMBER_CARD_OPEN_DELAY_MS);
                }}
              >
                <span className="member-card-row">
                  <span className="member-card-main">
                    <span className="member-card-name">{label}</span>
                    {/* Each channel identifier gets its own pill on its own
                        line. Stacked rather than in a row because a real
                        address and a real E.164 number cannot both survive
                        390px side by side — one of them always truncated to
                        uselessness. */}
                    <span className="member-card-contact">
                      {email !== "" ? (
                        <span className="member-card-pill" title="Email">
                          {email}
                        </span>
                      ) : null}
                      {phone !== "" ? (
                        <span className="member-card-pill" title="Phone">
                          {phone}
                        </span>
                      ) : null}
                      {insta !== "" ? (
                        <span className="member-card-pill" title="Instagram">
                          @{insta}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {/* The three numbers an operator scans down the right edge,
                      one per line and right-aligned so they form a column that
                      reads vertically across cards. All one colour: they are
                      the same KIND of fact, and the old mix (grey date, orange
                      credits, grey spend) implied a hierarchy that isn't
                      there. */}
                  <span className="member-card-stats">
                    {nextSession !== "" ? (
                      <span
                        className="member-card-next"
                        title="Next booked session"
                      >
                        {nextSession}
                      </span>
                    ) : null}
                    <span
                      className="member-card-credits"
                      title="Remaining credits"
                    >
                      {Number.isFinite(credits) ? credits : 0}{" "}
                      {credits === 1 ? "credit" : "credits"}
                    </span>
                    <span className="member-card-spend" title="Lifetime spent">
                      {fmtCardPoundsMinor(row["lifetime_spent_minor"])}
                    </span>
                  </span>
                </span>
              </button>
              {open ? (
                <div
                  className="member-card-actions"
                  role="group"
                  aria-label={`Quick actions for ${label}`}
                >
                  <button
                    type="button"
                    className="member-card-action"
                    onClick={() => {
                      setOpenId(null);
                      onEdit(row);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="member-card-action member-card-action-delete"
                    onClick={() => {
                      setOpenId(null);
                      onDelete(row);
                    }}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {hasMore ? (
        <LoadMoreSentinel onVisible={onLoadMore} loading={loadingMore} />
      ) : null}
      <div className="member-card-list-status">
        {loadingMore
          ? "Loading more…"
          : hasMore
            ? `${rows.length} loaded — scroll for more`
            : `All ${rows.length} loaded`}
      </div>
    </div>
  );
}

function DataTable({
  response,
  declutter = false,
  hidePii,
  onToggleHide,
  activeOrder,
  onSort,
  onRowClick,
  entity,
  onDeleteRow,
  configKey,
  activeFields,
  onToggleField,
  extraRows,
  onLoadMore,
  hasMore,
  loadingMore,
  selection,
  onStartSelection,
  onCancelSelection,
  onToggleSelect,
  onDeleteSelected,
  onDeleteAll,
  canDeleteAll,
  compactTools = false,
  leading,
}: DataTableProps): JSX.Element {
  const {
    rows: pageRows,
    pii_columns,
    count_returned,
    ordering_column,
    sortable_columns,
    next_cursor,
    available_fields,
  } = response;
  // One continuous list: the first batch plus everything scrolled in below it.
  const rows =
    extraRows.length > 0 ? [...pageRows, ...extraRows] : pageRows;
  const selecting = selection !== null;
  const piiSet = new Set(pii_columns);
  const hasPii = pii_columns.length > 0;
  const headers =
    rows.length > 0 && rows[0] !== undefined ? Object.keys(rows[0]) : [];
  const headersKey = headers.join(",");
  const clickable = onRowClick !== null;

  // --- double-click-to-copy ---
  // Double-clicking a cell copies its raw value to the clipboard so it can be
  // pasted elsewhere. `copiedKey` (`<rowIndex>:<header>`) drives a brief flash
  // on the cell that was copied. For clickable rows (entities with a row-edit
  // action) a single click opens the edit modal — so the row onClick is
  // debounced and cancelled when a double-click arrives, letting one gesture
  // copy without also opening the modal.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // Open chat-thread modal (members `chat_thread` cell double-click).
  const [chatRow, setChatRow] = useState<Record<string, unknown> | null>(null);
  const copyFlashRef = useRef<number | null>(null);
  const rowClickTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyFlashRef.current !== null) window.clearTimeout(copyFlashRef.current);
      if (rowClickTimerRef.current !== null)
        window.clearTimeout(rowClickTimerRef.current);
    },
    [],
  );
  const copyCell = (value: unknown, cellKey: string): void => {
    const text = copyableCellValue(value);
    if (text === "") return;
    const flash = (): void => {
      setCopiedKey(cellKey);
      if (copyFlashRef.current !== null) window.clearTimeout(copyFlashRef.current);
      copyFlashRef.current = window.setTimeout(() => {
        setCopiedKey(null);
        copyFlashRef.current = null;
      }, 900);
    };
    const clip = navigator.clipboard;
    if (clip !== undefined && typeof clip.writeText === "function") {
      void clip.writeText(text).then(flash, () => {
        /* clipboard blocked (insecure context / denied) — no flash */
      });
    }
  };

  // --- persisted layout state (lazy-init from localStorage, keyed by configKey) ---
  const init = useRef<Partial<TableConfig>>(loadConfig(configKey)).current;
  // Per-entity default layout (used only when nothing is saved + after Reset).
  const layoutDefault = DEFAULT_LAYOUT[entity];
  const [order, setOrder] = useState<string[]>(
    init.order ?? layoutDefault?.order ?? headers,
  );
  const [widths, setWidths] = useState<Record<string, number>>(
    init.widths ?? {},
  );
  const [additional, setAdditional] = useState<string[]>(
    init.additional ?? layoutDefault?.additional ?? [],
  );
  const [hidden, setHidden] = useState<string[]>(init.hidden ?? []);
  const [rowHeight, setRowHeight] = useState<number>(init.rowHeight ?? 0);
  const [hideAdditional, setHideAdditional] = useState<boolean>(
    init.hideAdditional ?? layoutDefault?.hideAdditional ?? false,
  );
  // "+" add-field menu open state.
  const [fieldMenuOpen, setFieldMenuOpen] = useState<boolean>(false);
  const fieldMenuRef = useRef<HTMLDivElement | null>(null);
  // "Columns" show/hide menu open state.
  const [colMenuOpen, setColMenuOpen] = useState<boolean>(false);
  const colMenuRef = useRef<HTMLDivElement | null>(null);
  // Compact ("+") tools menu — every toolbar control behind one trigger.
  const [toolsOpen, setToolsOpen] = useState<boolean>(false);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const addableFields = available_fields ?? [];
  const activeFieldSet = new Set(activeFields);

  // Close the compact tools menu on an outside click.
  useEffect(() => {
    if (!toolsOpen) return undefined;
    const onDown = (ev: MouseEvent): void => {
      if (toolsRef.current && !toolsRef.current.contains(ev.target as Node)) {
        setToolsOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [toolsOpen]);

  // Close the add-field menu on an outside click.
  useEffect(() => {
    if (!fieldMenuOpen) return undefined;
    const onDown = (ev: MouseEvent): void => {
      if (
        fieldMenuRef.current &&
        !fieldMenuRef.current.contains(ev.target as Node)
      ) {
        setFieldMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [fieldMenuOpen]);

  // Close the columns menu on an outside click.
  useEffect(() => {
    if (!colMenuOpen) return undefined;
    const onDown = (ev: MouseEvent): void => {
      if (
        colMenuRef.current &&
        !colMenuRef.current.contains(ev.target as Node)
      ) {
        setColMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [colMenuOpen]);

  // Reconcile saved order with the live header set (new columns appended,
  // vanished columns dropped) whenever the columns change.
  useEffect(() => {
    setOrder((prev) => {
      const existing = prev.filter((h) => headers.includes(h));
      const added = headers.filter((h) => !existing.includes(h));
      const merged = [...existing, ...added];
      return arraysEqual(merged, prev) ? prev : merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headersKey]);

  // Persist on any layout change.
  useEffect(() => {
    try {
      localStorage.setItem(
        LS_PREFIX + configKey + layoutVersionSuffix(configKey),
        JSON.stringify({
          order,
          widths,
          additional,
          hidden,
          rowHeight,
          hideAdditional,
        }),
      );
    } catch {
      // localStorage may be unavailable (private mode) — non-fatal.
    }
  }, [configKey, order, widths, additional, hidden, rowHeight, hideAdditional]);

  const additionalSet = new Set(additional);
  const hiddenSet = new Set(hidden);
  // On the mobile Members tab (`declutter`) additional columns are always
  // collapsed — the toggle is hidden, so force it on for this render only
  // (the persisted `hideAdditional` state is never mutated → desktop keeps its
  // own preference).
  const effectiveHideAdditional = hideAdditional || declutter;
  const orderedHeaders = order.filter((h) => headers.includes(h));
  const visibleHeaders = orderedHeaders
    .filter((h) => !hiddenSet.has(h))
    .filter((h) => !(effectiveHideAdditional && additionalSet.has(h)));
  const visibleSet = new Set(visibleHeaders);
  const widthOf = (h: string): number => widths[h] ?? DEFAULT_COL_WIDTH;
  const tableWidth =
    visibleHeaders.reduce((s, h) => s + widthOf(h), 0) +
    (onDeleteRow !== null ? ACTIONS_COL_WIDTH : 0);

  // --- column resize ---
  const startResize = (e: React.MouseEvent, col: string): void => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widthOf(col);
    const onMove = (ev: MouseEvent): void => {
      const w = Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX));
      setWidths((prev) => ({ ...prev, [col]: w }));
    };
    const onUp = (): void => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // --- column drag-reorder (static headers + a drop-indicator line) ---
  const dragGeom = useRef<DragGeom | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [dragView, setDragView] = useState<DragView | null>(null);

  /** Nearest column boundary (0..n) to the cursor's table-content X. */
  const dropIndexFor = (
    g: DragGeom,
    clientX: number,
  ): { insertIndex: number; lineX: number } => {
    let x = clientX;
    if (g.scrollEl) {
      const rect = g.scrollEl.getBoundingClientRect();
      x = clientX - rect.left + g.scrollEl.scrollLeft;
    }
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < g.boundaries.length; i += 1) {
      const b = g.boundaries[i] ?? 0;
      const d = Math.abs(x - b);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return { insertIndex: best, lineX: g.boundaries[best] ?? 0 };
  };

  const startDrag = (e: React.MouseEvent, visIndex: number, col: string): void => {
    if (e.button !== 0) return;
    e.preventDefault(); // suppress native text selection during the drag
    const boundaries: number[] = [0];
    let acc = 0;
    for (const h of visibleHeaders) {
      acc += widthOf(h);
      boundaries.push(acc);
    }
    dragGeom.current = {
      col,
      fromVis: visIndex,
      startX: e.clientX,
      boundaries,
      visible: visibleHeaders.slice(),
      scrollEl: scrollRef.current,
      active: false,
    };
    const onMove = (ev: MouseEvent): void => {
      const g = dragGeom.current;
      if (!g) return;
      if (!g.active && Math.abs(ev.clientX - g.startX) < DRAG_THRESHOLD) return;
      g.active = true;
      const { insertIndex, lineX } = dropIndexFor(g, ev.clientX);
      setDragView({
        fromVis: g.fromVis,
        col: g.col,
        insertIndex,
        lineX,
        pointerX: ev.clientX,
        pointerY: ev.clientY,
      });
    };
    const onUp = (ev: MouseEvent): void => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const g = dragGeom.current;
      dragGeom.current = null;
      setDragView(null);
      if (!g || !g.active) return; // was a click / double-click, not a drag
      const { insertIndex } = dropIndexFor(g, ev.clientX);
      // Move the dragged column to the boundary; adjust for its own removal.
      const moved = g.visible.slice();
      const [c] = moved.splice(g.fromVis, 1);
      if (c === undefined) return;
      const target = insertIndex > g.fromVis ? insertIndex - 1 : insertIndex;
      moved.splice(target, 0, c);
      setOrder((prev) => {
        let vi = 0;
        return prev.map((h) => (visibleSet.has(h) ? (moved[vi++] ?? h) : h));
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const toggleAdditional = (col: string): void => {
    setAdditional((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  };

  const toggleHidden = (col: string): void => {
    setHidden((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  };

  const resetLayout = (): void => {
    // Reset to the per-entity default layout (Resend-style for
    // resend_message_events), falling back to the raw header order.
    setOrder(layoutDefault?.order ?? headers);
    setWidths({});
    setAdditional(layoutDefault?.additional ?? []);
    setHidden([]);
    setRowHeight(0);
    setHideAdditional(layoutDefault?.hideAdditional ?? false);
    try {
      localStorage.removeItem(
        LS_PREFIX + configKey + layoutVersionSuffix(configKey),
      );
    } catch {
      /* non-fatal */
    }
  };

  const additionalCount = orderedHeaders.filter((h) =>
    additionalSet.has(h),
  ).length;
  const hiddenCount = hidden.filter((h) => headers.includes(h)).length;

  return (
    <>
      {declutter ? null : compactTools ? (
        <div className="data-browser-toolbar data-browser-toolbar--compact">
          {leading}
          <div className="data-add-field" ref={toolsRef}>
            <button
              type="button"
              className="data-browser-pager-btn data-add-field-btn data-tools-btn"
              onClick={() => setToolsOpen((v) => !v)}
              aria-expanded={toolsOpen}
              aria-label="Table tools"
              title="Columns, row height, layout and delete tools"
            >
              +
            </button>
            {toolsOpen ? (
              <div className="data-add-field-menu" role="menu">
                <div className="data-add-field-menu-head">View</div>
                <label className="data-add-field-option">
                  <span>Row height</span>
                  <select
                    value={rowHeight}
                    onChange={(e) => setRowHeight(Number(e.target.value))}
                  >
                    {ROW_HEIGHTS.map((rh) => (
                      <option key={rh.label} value={rh.px}>
                        {rh.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="data-add-field-option">
                  <input
                    type="checkbox"
                    checked={hideAdditional}
                    onChange={() => setHideAdditional((v) => !v)}
                  />
                  <span>
                    Hide additional
                    {additionalCount > 0 ? ` (${additionalCount})` : ""}
                  </span>
                </label>
                {hasPii ? (
                  <label className="data-add-field-option">
                    <input
                      type="checkbox"
                      checked={hidePii}
                      onChange={onToggleHide}
                    />
                    <span>Hide PII</span>
                  </label>
                ) : null}
                {headers.length > 0 ? (
                  <>
                    <div className="data-add-field-menu-head">
                      Show columns
                      {hiddenCount > 0
                        ? ` (${headers.length - hiddenCount}/${headers.length})`
                        : ""}
                    </div>
                    {orderedHeaders.map((h) => (
                      <label key={h} className="data-add-field-option">
                        <input
                          type="checkbox"
                          checked={!hiddenSet.has(h)}
                          onChange={() => toggleHidden(h)}
                        />
                        <span>{h}</span>
                        {piiSet.has(h) ? (
                          <span className="data-browser-pii-marker"> · PII</span>
                        ) : null}
                      </label>
                    ))}
                  </>
                ) : null}
                {addableFields.length > 0 ? (
                  <>
                    <div className="data-add-field-menu-head">
                      Related columns
                    </div>
                    {addableFields.map((f) => (
                      <label key={f.key} className="data-add-field-option">
                        <input
                          type="checkbox"
                          checked={activeFieldSet.has(f.key)}
                          onChange={() => onToggleField(f.key)}
                        />
                        <span>{f.label}</span>
                        {f.pii ? (
                          <span className="data-browser-pii-marker"> · PII</span>
                        ) : null}
                      </label>
                    ))}
                  </>
                ) : null}
                <div className="data-add-field-menu-head">Table</div>
                <button
                  type="button"
                  className="data-add-field-action"
                  onClick={() => {
                    resetLayout();
                    setToolsOpen(false);
                  }}
                >
                  Reset layout
                </button>
                {onDeleteRow !== null ? (
                  <button
                    type="button"
                    className="data-add-field-action"
                    onClick={() => {
                      if (selecting) onCancelSelection();
                      else onStartSelection();
                      setToolsOpen(false);
                    }}
                  >
                    {selecting ? "Cancel selection" : "Delete"}
                  </button>
                ) : null}
                {onDeleteRow !== null && canDeleteAll ? (
                  <button
                    type="button"
                    className="data-add-field-action data-browser-danger-btn"
                    onClick={() => {
                      onDeleteAll();
                      setToolsOpen(false);
                    }}
                  >
                    Delete all
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
      <div className="data-browser-toolbar">
        <div className="data-browser-toolbar-meta">
          <strong>{response.entity}</strong> · {count_returned} row
          {count_returned === 1 ? "" : "s"} · ordered by{" "}
          <code>{ordering_column}</code>{" "}
          {activeOrder?.endsWith("_asc") ? "ASC" : "DESC"}
          {hasPii ? (
            <>
              {" "}
              ·{" "}
              <code>
                {pii_columns.length} PII column
                {pii_columns.length === 1 ? "" : "s"}
              </code>
            </>
          ) : null}
        </div>
        <div
          className="data-browser-toolbar-controls"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <label className="data-browser-reveal-toggle">
            <span>Row height</span>
            <select
              value={rowHeight}
              onChange={(e) => setRowHeight(Number(e.target.value))}
            >
              {ROW_HEIGHTS.map((rh) => (
                <option key={rh.label} value={rh.px}>
                  {rh.label}
                </option>
              ))}
            </select>
          </label>
          <label className="data-browser-reveal-toggle">
            <input
              type="checkbox"
              checked={hideAdditional}
              onChange={() => setHideAdditional((v) => !v)}
            />
            Hide additional{additionalCount > 0 ? ` (${additionalCount})` : ""}
          </label>
          {hasPii ? (
            <label className="data-browser-reveal-toggle">
              <input type="checkbox" checked={hidePii} onChange={onToggleHide} />
              Hide PII
            </label>
          ) : null}
          {headers.length > 0 ? (
            <div className="data-add-field" ref={colMenuRef}>
              <button
                type="button"
                className="data-browser-pager-btn data-add-field-btn"
                onClick={() => setColMenuOpen((v) => !v)}
                aria-expanded={colMenuOpen}
                title="Choose which columns are shown in this table"
              >
                Columns{hiddenCount > 0 ? ` (${headers.length - hiddenCount}/${headers.length})` : ""}
              </button>
              {colMenuOpen ? (
                <div className="data-add-field-menu" role="menu">
                  <div className="data-add-field-menu-head">Show columns</div>
                  {orderedHeaders.map((h) => (
                    <label key={h} className="data-add-field-option">
                      <input
                        type="checkbox"
                        checked={!hiddenSet.has(h)}
                        onChange={() => toggleHidden(h)}
                      />
                      <span>{h}</span>
                      {piiSet.has(h) ? (
                        <span className="data-browser-pii-marker"> · PII</span>
                      ) : null}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {addableFields.length > 0 ? (
            <div className="data-add-field" ref={fieldMenuRef}>
              <button
                type="button"
                className="data-browser-pager-btn data-add-field-btn"
                onClick={() => setFieldMenuOpen((v) => !v)}
                aria-expanded={fieldMenuOpen}
                title="Add a related column (e.g. trainer name) to this table"
              >
                + Add field
                {activeFields.length > 0 ? ` (${activeFields.length})` : ""}
              </button>
              {fieldMenuOpen ? (
                <div className="data-add-field-menu" role="menu">
                  <div className="data-add-field-menu-head">
                    Related columns
                  </div>
                  {addableFields.map((f) => (
                    <label key={f.key} className="data-add-field-option">
                      <input
                        type="checkbox"
                        checked={activeFieldSet.has(f.key)}
                        onChange={() => onToggleField(f.key)}
                      />
                      <span>{f.label}</span>
                      {f.pii ? (
                        <span className="data-browser-pii-marker"> · PII</span>
                      ) : null}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="data-browser-pager-btn"
            onClick={resetLayout}
            title="Reset column order, widths, and additional/essential marks for this table"
          >
            Reset layout
          </button>
          {onDeleteRow !== null ? (
            <button
              type="button"
              className="data-browser-pager-btn"
              onClick={selecting ? onCancelSelection : onStartSelection}
              title={
                selecting
                  ? "Leave selection mode"
                  : "Pick rows to delete — click rows to select them"
              }
            >
              {selecting ? "Cancel selection" : "Delete"}
            </button>
          ) : null}
          {onDeleteRow !== null && canDeleteAll ? (
            <button
              type="button"
              className="data-browser-pager-btn data-browser-danger-btn"
              onClick={onDeleteAll}
              title={`Empty the ${entity} table`}
            >
              Delete all
            </button>
          ) : null}
        </div>
      </div>
      )}
      {/*
        Sits ABOVE the table. It used to live under it, which infinite scroll
        made unusable: every appended page pushed it further down, so reaching
        "Delete selected" meant scrolling to the bottom — which loaded another
        page and moved it again.
      */}
      {selecting ? (
        <div className="data-browser-selection-bar">
          <span>
            {selection?.size ?? 0} selected
            {(selection?.size ?? 0) === 0
              ? " · click rows to pick them"
              : null}
          </span>
          <button
            type="button"
            className="data-browser-pager-btn data-browser-danger-btn"
            disabled={(selection?.size ?? 0) === 0}
            onClick={onDeleteSelected}
          >
            Delete selected
          </button>
          <button
            type="button"
            className="data-browser-pager-btn"
            onClick={onCancelSelection}
          >
            Cancel
          </button>
        </div>
      ) : null}
      {dragView !== null ? (
        // Floating ghost of the grabbed header — position:fixed so it
        // follows the cursor in the foreground and is never clipped by the
        // table's horizontal-scroll container.
        <div
          className="data-drag-ghost"
          style={{ left: `${dragView.pointerX}px`, top: `${dragView.pointerY}px` }}
        >
          <code>{dragView.col}</code>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="empty">No rows.</p>
      ) : (
        <div className="table-scroll-x" ref={scrollRef}>
          <div
            className="data-table-wrap"
            style={{ position: "relative", width: `${tableWidth}px` }}
          >
            {dragView !== null ? (
              <div
                className="data-drop-line"
                style={{ left: `${dragView.lineX}px` }}
              />
            ) : null}
            <table
              className={`data-table-resizable${dragView !== null ? " data-table-dragging" : ""}${rowHeight === 30 ? " rh-compact" : rowHeight === 60 ? " rh-tall" : ""}`}
              style={{ width: `${tableWidth}px`, tableLayout: "fixed" }}
            >
              <colgroup>
                {visibleHeaders.map((h) => (
                  <col key={h} style={{ width: `${widthOf(h)}px` }} />
                ))}
                {onDeleteRow !== null ? (
                  <col style={{ width: `${ACTIONS_COL_WIDTH}px` }} />
                ) : null}
              </colgroup>
              <thead>
                <tr>
                  {visibleHeaders.map((h, vi) => {
                    const isDragged = dragView?.fromVis === vi;
                    return (
                      <th
                        key={h}
                        className={
                          (isDragged ? "data-th-grabbed " : "") +
                          (additionalSet.has(h) ? "data-th-additional" : "")
                        }
                      >
                        <div className="data-th-inner">
                          <span
                            className="data-th-label"
                            onMouseDown={(e) => startDrag(e, vi, h)}
                            onDoubleClick={() => toggleAdditional(h)}
                            title="Drag to reorder · double-click to mark additional"
                          >
                            <code>{h}</code>
                            {piiSet.has(h) ? (
                              <span className="data-browser-pii-marker">
                                {" "}
                                · PII
                              </span>
                            ) : null}
                            {additionalSet.has(h) ? (
                              <span className="data-col-additional-badge">
                                {" "}
                                · additional
                              </span>
                            ) : null}
                          </span>
                          {(() => {
                            // Click-to-sort caret for columns the server marks
                            // sortable (members: codex #, name, status, joined).
                            // Its own button (not the drag/double-click label),
                            // so the gestures never collide. ▼ = active DESC,
                            // ▲ = active ASC, ↕ = inactive (click to sort DESC).
                            const spec = onSort
                              ? sortable_columns.find((s) => s.header === h)
                              : undefined;
                            if (spec === undefined) return null;
                            const isDesc = activeOrder === spec.desc_order;
                            const isAsc = activeOrder === spec.asc_order;
                            return (
                              <button
                                type="button"
                                className={
                                  "data-th-sort" +
                                  (isDesc || isAsc ? " data-th-sort-active" : "")
                                }
                                title={`Sort by ${h}`}
                                aria-label={`Sort by ${h}`}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSort?.(spec.desc_order, spec.asc_order);
                                }}
                              >
                                {isDesc ? "▼" : isAsc ? "▲" : "↕"}
                              </button>
                            );
                          })()}
                          <span
                            className="data-col-resize"
                            onMouseDown={(e) => startResize(e, h)}
                            role="separator"
                            aria-orientation="vertical"
                          />
                        </div>
                      </th>
                    );
                  })}
                  {onDeleteRow !== null ? (
                    <th className="data-th-actions" aria-label="Actions" />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rowId =
                    typeof row["id"] === "string" ? (row["id"] as string) : null;
                  const isSelected = rowId !== null && selection?.has(rowId) === true;
                  return (
                  <tr
                    key={i}
                    className={
                      [
                        clickable || selecting
                          ? "data-browser-row-clickable"
                          : null,
                        isSelected ? "data-browser-row-selected" : null,
                      ]
                        .filter((c) => c !== null)
                        .join(" ") || undefined
                    }
                    style={
                      rowHeight > 0 ? { height: `${rowHeight}px` } : undefined
                    }
                    onClick={
                      // Selection mode takes the row click over the edit modal:
                      // while picking rows to delete, opening an editor on every
                      // click would be unusable. Rows with no string `id` are
                      // not selectable because the delete endpoint keys on `id`
                      // and could not target them either.
                      selecting
                        ? () => {
                            if (rowId !== null) onToggleSelect(rowId);
                          }
                        : clickable && onRowClick !== null
                          ? () => {
                              // Debounce so a double-click (copy) can cancel the
                              // pending edit-modal open. See `copyCell`.
                              if (rowClickTimerRef.current !== null)
                                window.clearTimeout(rowClickTimerRef.current);
                              rowClickTimerRef.current = window.setTimeout(() => {
                                rowClickTimerRef.current = null;
                                onRowClick(row);
                              }, 200);
                            }
                          : undefined
                    }
                  >
                    {visibleHeaders.map((h) => {
                      const cell = row[h];
                      const isJson =
                        Array.isArray(cell) ||
                        (cell !== null && typeof cell === "object");
                      const cellKey = `${i}:${h}`;
                      const piiHidden = piiSet.has(h) && hidePii;
                      // `chat_thread` (members) renders as a height-clamped
                      // WhatsApp-style preview; double-clicking opens the full
                      // thread modal instead of copying the raw jsonb.
                      const isChat = h === "chat_thread";
                      // resend_message_events `event_type` renders as a colored
                      // status pill (Delivered / Opened / Clicked / Bounced /
                      // Complained) so the email log reads like Resend's own.
                      const isStatus =
                        entity === "resend_message_events" &&
                        h === "event_type" &&
                        typeof cell === "string";
                      // Members + trainers browsers — the `id` + `display_name`
                      // cells link to that entity's dedicated page.
                      // `stopPropagation` keeps the click from also opening the
                      // row-edit modal.
                      const detailHref =
                        (entity === "members" || entity === "trainers") &&
                        (h === "id" || h === "display_name") &&
                        typeof row["id"] === "string"
                          ? `/${entity}/${encodeURIComponent(String(row["id"]))}`
                          : null;
                      return (
                        <td
                          key={h}
                          className={
                            "data-cell-clip" +
                            (copiedKey === cellKey ? " data-cell-copied" : "") +
                            (isChat ? " data-cell-chat" : "")
                          }
                          title={
                            piiHidden
                              ? undefined
                              : isChat
                                ? "Double-click to open the full conversation"
                                : "Double-click to copy"
                          }
                          onMouseDown={(e) => {
                            // Suppress the native word-selection a double-click
                            // would otherwise paint — we copy the whole value
                            // programmatically, so no text selection is needed.
                            // `detail > 1` is the second+ click of the gesture;
                            // single-click drag-select stays intact.
                            if (e.detail > 1) e.preventDefault();
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (rowClickTimerRef.current !== null) {
                              window.clearTimeout(rowClickTimerRef.current);
                              rowClickTimerRef.current = null;
                            }
                            if (piiHidden) return;
                            if (isChat) {
                              setChatRow(row);
                              return;
                            }
                            copyCell(cell, cellKey);
                          }}
                        >
                          {piiHidden ? (
                            <span className="data-browser-pii-hidden">
                              {PII_PLACEHOLDER}
                            </span>
                          ) : isChat ? (
                            <ChatThreadPreview value={cell} />
                          ) : isStatus ? (
                            <span
                              className={`data-status-badge data-status-${String(
                                cell,
                              )}`}
                            >
                              {renderCellValue(cell)}
                            </span>
                          ) : detailHref !== null ? (
                            <Link
                              to={detailHref}
                              className="data-member-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <code className="data-browser-cell">
                                {renderCellValue(cell)}
                              </code>
                            </Link>
                          ) : isJson ? (
                            <CollapsibleJson value={cell} />
                          ) : (
                            <code className="data-browser-cell">
                              {renderCellValue(cell)}
                            </code>
                          )}
                        </td>
                      );
                    })}
                    {onDeleteRow !== null ? (
                      <td className="data-td-actions">
                        {typeof row["id"] === "string" ? (
                          <button
                            type="button"
                            className="data-row-delete-btn"
                            title={`Delete this ${entity} row`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (rowClickTimerRef.current !== null) {
                                window.clearTimeout(rowClickTimerRef.current);
                                rowClickTimerRef.current = null;
                              }
                              onDeleteRow(row);
                            }}
                          >
                            Delete
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {/*
              Infinite-scroll sentinel. Sits INSIDE the scroll container so the
              observer fires against the table's own overflow, and renders only
              while another batch exists — so at the end of the table there is
              nothing to trip and no request is made.
            */}
            {hasMore ? (
              <LoadMoreSentinel onVisible={onLoadMore} loading={loadingMore} />
            ) : null}
          </div>
        </div>
      )}
      {/*
        Scroll status. Replaced the Prev/Next paginator on 2026-07-29: the
        table is now one continuously-growing list, so a page number describes
        nothing an operator can act on. What IS worth stating is how much is
        loaded and whether the bottom is the real end of the table — without
        it, "no more rows appeared" and "the loader is still working" look
        identical.
      */}
      <div className="data-browser-scroll-status">
        {loadingMore
          ? "Loading more…"
          : hasMore
            ? `${rows.length} rows loaded — scroll for more`
            : `${rows.length} row${rows.length === 1 ? "" : "s"} — end of table`}
      </div>
      {chatRow !== null ? (
        <ChatThreadModal row={chatRow} onClose={() => setChatRow(null)} />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dependency-aware per-row delete modal. On open it runs a `check` (pure read)
// to surface every FK dependent, then offers:
//   - "Delete row" when nothing references it (safe single delete), or
//   - "Delete row + N dependents" (cascade) when there are dependents, after an
//     explicit second confirmation. The cascade is bounded server-side.
// ---------------------------------------------------------------------------

interface DeleteRowModalProps {
  readonly entity: string;
  readonly row: Record<string, unknown>;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

function DeleteRowModal({
  entity,
  row,
  onClose,
  onSuccess,
}: DeleteRowModalProps): JSX.Element {
  const id = typeof row["id"] === "string" ? (row["id"] as string) : "";
  const [phase, setPhase] = useState<
    "checking" | "ready" | "deleting" | "error"
  >("checking");
  const [dependents, setDependents] = useState<ReadonlyArray<DeleteDependent>>(
    [],
  );
  const [kept, setKept] = useState<ReadonlyArray<DeleteKept>>([]);
  const [confirmCascade, setConfirmCascade] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r: DataRowDeleteResponse = await dataRowDelete(
          entity,
          id,
          "check",
        );
        if (cancelled) return;
        if (r.inspection) {
          setDependents(r.inspection.dependents);
          setKept(r.inspection.kept ?? []);
          setPhase(r.inspection.exists ? "ready" : "error");
          if (!r.inspection.exists) setErrMsg("Row no longer exists.");
        } else {
          setPhase("error");
          setErrMsg(r.message ?? r.code ?? "Could not inspect row.");
        }
      } catch (e) {
        if (cancelled) return;
        setPhase("error");
        setErrMsg(e instanceof ApiError ? e.message : "Inspection failed.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entity, id]);

  const hasDependents = dependents.length > 0;

  const runDelete = async (cascade: boolean): Promise<void> => {
    setPhase("deleting");
    setErrMsg(null);
    try {
      const r = await dataRowDelete(entity, id, cascade ? "cascade" : "delete");
      if (r.ok) {
        onSuccess();
        return;
      }
      // 409 has_dependents (a dependent appeared since the check) — refresh
      // the blocker list and require the cascade opt-in.
      if (r.code === "has_dependents" && r.result?.blockers) {
        setDependents(r.result.blockers);
        setPhase("ready");
        setConfirmCascade(false);
        setErrMsg("This row has dependents — choose cascade to remove them.");
        return;
      }
      setPhase("error");
      setErrMsg(r.message ?? r.code ?? "Delete failed.");
    } catch (e) {
      setPhase("error");
      setErrMsg(e instanceof ApiError ? e.message : "Delete failed.");
    }
  };

  const totalDependentRows = dependents.reduce((a, d) => a + d.count, 0);

  return (
    <div className="modal-backdrop" {...useBackdropDismiss(onClose)}>
      <div
        className="modal-card data-delete-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>
          Delete <code>{entity}</code> row
        </h3>
        <p className="data-delete-id">
          <code>{id.slice(0, 8)}…</code>
        </p>

        {phase === "checking" ? (
          <p>Checking dependencies…</p>
        ) : phase === "error" ? (
          <p className="data-delete-error">{errMsg}</p>
        ) : (
          <>
            {hasDependents ? (
              <>
                <p className="data-delete-warning">
                  ⚠️ This row is referenced by {totalDependentRows} row
                  {totalDependentRows === 1 ? "" : "s"} in{" "}
                  {dependents.length} table
                  {dependents.length === 1 ? "" : "s"}. Deleting it requires
                  removing those first (cascade).
                </p>
                <ul className="data-delete-deps">
                  {dependents.map((d) => (
                    <li key={`${d.table}.${d.column}`}>
                      <code>{d.table}</code>
                      <span className="data-delete-dep-col">.{d.column}</span> ·{" "}
                      {d.count} row{d.count === 1 ? "" : "s"}
                    </li>
                  ))}
                </ul>
                <label className="data-delete-confirm">
                  <input
                    type="checkbox"
                    checked={confirmCascade}
                    onChange={(e) => setConfirmCascade(e.target.checked)}
                  />{" "}
                  I understand this will also delete the {totalDependentRows}{" "}
                  dependent row{totalDependentRows === 1 ? "" : "s"} above.
                </label>
              </>
            ) : (
              <p>No other rows reference this row. Safe to delete.</p>
            )}
            {kept.length > 0 ? (
              <p>
                Kept:{" "}
                {kept.map((k) => `${k.count} ${k.label}`).join("; ")}.
              </p>
            ) : null}
            {errMsg ? <p className="data-delete-error">{errMsg}</p> : null}
          </>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose} disabled={phase === "deleting"}>
            Cancel
          </button>
          {phase === "ready" || phase === "deleting" ? (
            hasDependents ? (
              <button
                type="button"
                className="data-row-delete-btn data-row-delete-danger"
                disabled={!confirmCascade || phase === "deleting"}
                onClick={() => void runDelete(true)}
              >
                {phase === "deleting"
                  ? "Deleting…"
                  : `Delete row + ${totalDependentRows} dependent${totalDependentRows === 1 ? "" : "s"}`}
              </button>
            ) : (
              <button
                type="button"
                className="data-row-delete-btn data-row-delete-danger"
                disabled={phase === "deleting"}
                onClick={() => void runDelete(false)}
              >
                {phase === "deleting" ? "Deleting…" : "Delete row"}
              </button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat thread (members `chat_thread` computed column) — a jsonb array of
// `{ ts, body, dir }` rows ('in' = from the member, 'out' = from the AI
// agent/studio), capped server-side at the 200 most-recent messages and
// returned chronologically. The preview clamps to a few rows; double-click
// opens the full WhatsApp-style modal.
// ---------------------------------------------------------------------------

interface ChatMessage {
  readonly ts: string | null;
  readonly body: string | null;
  readonly dir: "in" | "out";
}

function parseChatMessages(value: unknown): ChatMessage[] {
  if (!Array.isArray(value)) return [];
  const out: ChatMessage[] = [];
  for (const item of value) {
    if (item === null || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const dir = rec["dir"] === "out" ? "out" : "in";
    out.push({
      ts: typeof rec["ts"] === "string" ? (rec["ts"] as string) : null,
      body: typeof rec["body"] === "string" ? (rec["body"] as string) : null,
      dir,
    });
  }
  return out;
}

/**
 * A COUNT, not a transcript. Rendering every bubble made this one cell taller
 * than the whole rest of the row, so a table of members turned into a column
 * of stacked chat logs. The full thread is one double-click away in the modal,
 * which is where a conversation is actually readable.
 */
function ChatThreadPreview({ value }: { value: unknown }): JSX.Element {
  const messages = parseChatMessages(value);
  if (messages.length === 0) {
    return <code className="data-browser-cell">—</code>;
  }
  const inbound = messages.filter((m) => m.dir === "in").length;
  const outbound = messages.length - inbound;
  return (
    <span
      className="data-chat-count"
      title={`${messages.length} messages · ${inbound} received, ${outbound} sent — double-click to read`}
    >
      <strong>{messages.length}</strong>
      <span className="data-chat-count-split">
        {inbound} in · {outbound} out
      </span>
    </span>
  );
}

interface ChatThreadModalProps {
  readonly row: Record<string, unknown>;
  readonly onClose: () => void;
}

function ChatThreadModal({ row, onClose }: ChatThreadModalProps): JSX.Element {
  const messages = parseChatMessages(row["chat_thread"]);
  const who =
    typeof row["display_name"] === "string" && row["display_name"] !== ""
      ? (row["display_name"] as string)
      : typeof row["phone"] === "string"
        ? (row["phone"] as string)
        : "member";

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal data-chat-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-thread-modal-title"
      >
        <h2 id="chat-thread-modal-title" className="row-edit-modal-title">
          Conversation · <code>{who}</code>
        </h2>
        <p className="row-edit-modal-note">
          {messages.length} message{messages.length === 1 ? "" : "s"} (most
          recent 200). Member on the right, studio / AI agent on the left.
        </p>
        <div className="data-chat-log">
          {messages.length === 0 ? (
            <p className="empty">No messages.</p>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={
                  m.dir === "out"
                    ? "data-chat-row data-chat-row-out"
                    : "data-chat-row data-chat-row-in"
                }
              >
                <div
                  className={
                    m.dir === "out"
                      ? "data-chat-msg data-chat-msg-out"
                      : "data-chat-msg data-chat-msg-in"
                  }
                >
                  <div className="data-chat-msg-body">{m.body ?? "[media]"}</div>
                  {m.ts !== null ? (
                    <div className="data-chat-msg-ts">
                      {renderCellValue(m.ts)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function renderCellValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") {
    if (ISO_TIMESTAMP_RE.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return studioFormat(STUDIO_TIME_OPTS).format(d);
    }
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v instanceof Date) return studioFormat(STUDIO_TIME_OPTS).format(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// Double-click-to-copy (data browser): the string written to the clipboard
// when a user double-clicks a cell. Unlike `renderCellValue`, this returns the
// RAW underlying value — the original ISO timestamp / unformatted string — so
// the pasted value round-trips into a filter, query, or another tool rather
// than the localized display string. Null/undefined yield "" (nothing useful
// to paste).
function copyableCellValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v instanceof Date) return v.toISOString();
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// ---------------------------------------------------------------------------
// Phase 14 — UpdateMemberModal
// ---------------------------------------------------------------------------

interface UpdateMemberModalProps {
  readonly row: Record<string, unknown>;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

const STATUS_OPTIONS = ["active", "suspended", "deleted"] as const;

type StatusOption = (typeof STATUS_OPTIONS)[number];

interface FormState {
  readonly display_name: string;
  readonly phone: string;
  readonly status: StatusOption;
}

function statusFromRow(row: Record<string, unknown>): StatusOption {
  const v = row["status"];
  if (v === "suspended" || v === "deleted") return v;
  return "active";
}

function stringFromRow(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  return typeof v === "string" ? v : "";
}

function UpdateMemberModal({
  row,
  onClose,
  onSuccess,
}: UpdateMemberModalProps): JSX.Element {
  const memberId =
    typeof row["id"] === "string" ? (row["id"] as string) : "";
  // PII fields are visible in the modal regardless of the page's
  // global "Hide PII" toggle — editing requires seeing the values.
  // This is an explicit reveal moment, distinct from passive
  // browsing.
  const initial: FormState = {
    display_name: stringFromRow(row, "display_name"),
    phone: stringFromRow(row, "phone"),
    status: statusFromRow(row),
  };
  const [form, setForm] = useState<FormState>(initial);
  const dispatch = useDispatch<
    {
      readonly member_id: string;
      readonly patch: Record<string, unknown>;
    },
    Record<string, unknown>
  >("update_member");

  // ESC + click-outside close. Wired via a key listener on mount.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Compute the patch — only keys whose value differs from the
  // initial row value land in the dispatch. If empty, Save is
  // disabled. We build via a mutable record (not the readonly
  // `Partial<FormState>` shape) since FormState has `readonly`
  // fields; the dispatch wrapper accepts a partial wire object.
  const mutablePatch: Record<string, unknown> = {};
  if (form.display_name !== initial.display_name) {
    mutablePatch["display_name"] = form.display_name;
  }
  if (form.phone !== initial.phone) {
    // A cleared field sends `null` (removes the stored number); an empty
    // string would fail the E.164 schema. Trim so trailing spaces don't
    // sneak past the pattern.
    const trimmedPhone = form.phone.trim();
    mutablePatch["phone"] = trimmedPhone === "" ? null : trimmedPhone;
  }
  if (form.status !== initial.status) {
    mutablePatch["status"] = form.status;
  }
  const hasPatch = Object.keys(mutablePatch).length > 0;

  const onSubmit = async (): Promise<void> => {
    if (!hasPatch || dispatch.loading || memberId === "") return;
    const r = await dispatch.run({
      member_id: memberId,
      patch: mutablePatch,
    });
    if (r.ok) {
      onSuccess();
    }
    // else: state.error is rendered inline and the modal stays open
    // so the user can retry.
  };

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="row-edit-modal-title"
      >
        <h2 id="row-edit-modal-title" className="row-edit-modal-title">
          Edit member · <code>{memberId.slice(0, 8)}…</code>
        </h2>
        <p className="row-edit-modal-note">
          PII shown for editing. Email, consent, metadata, and system
          columns are not editable at phase 14.
        </p>
        <div className="row-edit-modal-form">
          <label className="row-edit-modal-label" htmlFor="rem-display_name">
            <span>display_name</span>
            <input
              id="rem-display_name"
              type="text"
              maxLength={100}
              value={form.display_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, display_name: e.target.value }))
              }
            />
          </label>
          <label className="row-edit-modal-label" htmlFor="rem-phone">
            <span>phone (E.164, e.g. +447700900000)</span>
            <input
              id="rem-phone"
              type="text"
              value={form.phone}
              onChange={(e) =>
                setForm((f) => ({ ...f, phone: e.target.value }))
              }
            />
          </label>
          <label className="row-edit-modal-label" htmlFor="rem-status">
            <span>status</span>
            <select
              id="rem-status"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  status: e.target.value as StatusOption,
                }))
              }
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        {dispatch.error !== null ? (
          <div className="row-edit-modal-error">
            <strong>Error:</strong>{" "}
            <code className="tag tag-error">{dispatch.error.code}</code>
            <p>{dispatch.error.message}</p>
          </div>
        ) : null}
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="row-edit-modal-save"
            disabled={!hasPatch || dispatch.loading}
            onClick={() => void onSubmit()}
          >
            {dispatch.loading
              ? "Saving…"
              : hasPatch
                ? `Save (${Object.keys(mutablePatch).length} change${Object.keys(mutablePatch).length === 1 ? "" : "s"})`
                : "No changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 38 — PaymentReconciliationModal
//
// Surfaces ONE OR BOTH of `reconcile_orphan_payment` + `unlink_payment`
// per row based on the row's current `member_id`:
//   - member_id IS NULL → reconcile button visible by default (member_id
//     input + optional reason); unlink button hidden.
//   - member_id IS NON-NULL → unlink button visible; reconcile button
//     hidden behind a "Retarget to different member" link that reveals
//     the input form (allows cross-member retargeting per sub-decision L).
// ---------------------------------------------------------------------------

interface PaymentReconciliationModalProps {
  readonly row: Record<string, unknown>;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

function PaymentReconciliationModal({
  row,
  onClose,
  onSuccess,
}: PaymentReconciliationModalProps): JSX.Element {
  const paymentId =
    typeof row["id"] === "string" ? (row["id"] as string) : "";
  const currentMemberId =
    typeof row["member_id"] === "string"
      ? (row["member_id"] as string)
      : null;
  const isOrphan = currentMemberId === null;

  const [memberInput, setMemberInput] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [showRetargetForm, setShowRetargetForm] = useState<boolean>(false);

  const reconcileDispatch = useDispatch<
    {
      readonly payment_id: string;
      readonly member_id: string;
      readonly reason?: string;
    },
    Record<string, unknown>
  >("reconcile_orphan_payment");
  const unlinkDispatch = useDispatch<
    { readonly payment_id: string; readonly reason?: string },
    Record<string, unknown>
  >("unlink_payment");

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onReconcile = async (): Promise<void> => {
    if (memberInput.length === 0 || reconcileDispatch.loading) return;
    const body: {
      payment_id: string;
      member_id: string;
      reason?: string;
    } = { payment_id: paymentId, member_id: memberInput };
    if (reason.length > 0) body.reason = reason;
    const r = await reconcileDispatch.run(body);
    if (r.ok) onSuccess();
  };

  const onUnlink = async (): Promise<void> => {
    if (unlinkDispatch.loading) return;
    const body: { payment_id: string; reason?: string } = {
      payment_id: paymentId,
    };
    if (reason.length > 0) body.reason = reason;
    const r = await unlinkDispatch.run(body);
    if (r.ok) onSuccess();
  };

  const reconcileFormVisible = isOrphan || showRetargetForm;
  const unlinkButtonVisible = !isOrphan;
  const err = reconcileDispatch.error ?? unlinkDispatch.error;
  const loading = reconcileDispatch.loading || unlinkDispatch.loading;

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-reconcile-modal-title"
      >
        <h2
          id="payment-reconcile-modal-title"
          className="row-edit-modal-title"
        >
          {isOrphan ? "Reconcile" : "Edit"} payment ·{" "}
          <code>{paymentId.slice(0, 8)}…</code>
        </h2>
        <p className="row-edit-modal-note">
          {isOrphan
            ? "Orphan payment (no member). Enter the target member_id (UUID v7) to link this row."
            : `Currently linked to member ${currentMemberId?.slice(0, 8)}…. You can unlink, or retarget to a different member.`}
        </p>
        {reconcileFormVisible ? (
          <div className="row-edit-modal-form">
            <label
              className="row-edit-modal-label"
              htmlFor="prm-member-id"
            >
              <span>member_id (UUID v7)</span>
              <input
                id="prm-member-id"
                type="text"
                value={memberInput}
                placeholder="018f0000-0000-7000-8000-000000000aaa"
                onChange={(e) => setMemberInput(e.target.value)}
              />
            </label>
          </div>
        ) : null}
        {!isOrphan && !showRetargetForm ? (
          <p className="row-edit-modal-note">
            <button
              type="button"
              className="row-edit-modal-cancel"
              onClick={() => setShowRetargetForm(true)}
            >
              Retarget to different member…
            </button>
          </p>
        ) : null}
        <div className="row-edit-modal-form">
          <label className="row-edit-modal-label" htmlFor="prm-reason">
            <span>reason (optional; max 500 chars)</span>
            <input
              id="prm-reason"
              type="text"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </div>
        {err !== null ? (
          <div className="row-edit-modal-error">
            <strong>Error:</strong>{" "}
            <code className="tag tag-error">{err.code}</code>
            <p>{err.message}</p>
          </div>
        ) : null}
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          {unlinkButtonVisible ? (
            <button
              type="button"
              className="row-edit-modal-save"
              disabled={loading}
              onClick={() => void onUnlink()}
            >
              {unlinkDispatch.loading ? "Unlinking…" : "Unlink"}
            </button>
          ) : null}
          {reconcileFormVisible ? (
            <button
              type="button"
              className="row-edit-modal-save"
              disabled={loading || memberInput.length === 0}
              onClick={() => void onReconcile()}
            >
              {reconcileDispatch.loading
                ? "Reconciling…"
                : isOrphan
                  ? "Reconcile"
                  : "Retarget"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The /data "Purge members" button renders only when the SPA was BUILT with
 * `LAYER_PUB_ALLOW_MEMBER_PURGE=1` (a Vite `envPrefix` var, baked at build
 * time — see `apps/dashboard/Dockerfile`). Visibility only: the server's
 * `LAYER_ALLOW_MEMBER_PURGE` is the real gate and 404s the route without it,
 * which the modal surfaces as "disabled on this deployment".
 */
const MEMBER_PURGE_BUTTON_VISIBLE =
  import.meta.env.LAYER_PUB_ALLOW_MEMBER_PURGE === "1";

// ---------------------------------------------------------------------------
// ResetDbModal — dev-only DB wipe back to default state.
//
// Requires typing `RESET` to confirm (mirrors phase 27's delete_member
// modal protocol). On success the parent component banners the result
// and re-fetches the current entity page.
// ---------------------------------------------------------------------------

interface ResetDbModalProps {
  readonly onClose: () => void;
  readonly onSuccess: (result: DevResetResponse) => void;
}

interface PurgeMembersModalProps {
  readonly onClose: () => void;
  readonly onSuccess: (result: MemberPurgeResponse) => void;
}

interface BulkDeleteModalProps {
  readonly entity: string;
  readonly target:
    | { readonly kind: "selected"; readonly ids: ReadonlyArray<string> }
    | { readonly kind: "all" };
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

/**
 * Confirmation for the two bulk deletes (2026-07-28).
 *
 * "Delete selected" runs a preflight first and shows ONE combined dependency
 * summary for the whole selection, so the operator answers the cascade
 * question once instead of per row.
 *
 * "Delete all" is irreversible and has no row to point at, so it asks for an
 * explicit yes — one deliberate click on a button that says what it does,
 * rather than a typed table name (dropped 2026-07-29: the friction was paid on
 * every use and bought nothing here, because the button is already behind a
 * modal and the destructive tables are refused outright by
 * `DELETE_ALL_PROTECTED`). The server still demands its own explicit
 * `confirm` flag, so a stray request cannot empty a table.
 */
function BulkDeleteModal({
  entity,
  target,
  onClose,
  onSuccess,
}: BulkDeleteModalProps): JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [blockers, setBlockers] = useState<ReadonlyArray<DeleteDependent>>([]);
  const [checked, setChecked] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const isAll = target.kind === "all";

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, loading]);

  // Preflight the selection so the dependency summary is on screen BEFORE the
  // operator commits to anything.
  useEffect(() => {
    if (isAll) return;
    let cancelled = false;
    void dataRowDeleteMany(entity, target.ids, "check")
      .then((r) => {
        if (cancelled) return;
        const deps = (
          r.inspection as { dependents?: ReadonlyArray<DeleteDependent> } | undefined
        )?.dependents;
        setBlockers(deps ?? []);
        setChecked(true);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setErr(e.message);
          setChecked(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, isAll]);

  const canConfirm = isAll
    ? !loading
    : checked && blockers.length === 0 && !loading;

  const onConfirm = async (): Promise<void> => {
    if (!canConfirm) return;
    setLoading(true);
    setErr(null);
    try {
      const r = isAll
        ? await dataRowDeleteAll(entity)
        : await dataRowDeleteMany(entity, target.ids, "delete");
      if (!r.ok) {
        setErr(r.code ?? "delete_failed");
        setBlockers(r.result?.blockers ?? []);
        setLoading(false);
        return;
      }
      onSuccess();
    } catch (e) {
      setErr((e as Error).message);
      setLoading(false);
    }
  };

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(loading ? () => undefined : onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-delete-modal-title"
      >
        <h2 id="bulk-delete-modal-title" className="row-edit-modal-title">
          {isAll
            ? `Empty the ${entity} table`
            : `Delete ${target.kind === "selected" ? target.ids.length : 0} ${entity} row(s)`}
        </h2>
        {isAll ? (
          <p>
            This deletes <strong>every row</strong> in <code>{entity}</code>. It
            cannot be undone, and it does <strong>not</strong> cascade — if
            another table references these rows the delete is refused and
            nothing changes.
          </p>
        ) : !checked ? (
          <p>Checking what references these rows…</p>
        ) : blockers.length > 0 ? (
          <>
            <p>
              These rows cannot be deleted — other tables reference them.
              Delete those rows first:
            </p>
            <ul className="data-delete-dependents">
              {blockers.map((b) => (
                <li key={`${b.table}.${b.column}`}>
                  <code>{b.table}</code>.<code>{b.column}</code>
                  {b.count > 0 ? ` — ${b.count} row(s)` : null}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p>
            Nothing references these rows. They will be deleted together — if
            any one fails, the whole batch rolls back.
          </p>
        )}
        {err !== null ? <p className="row-edit-modal-error">{err}</p> : null}
        <div className="row-edit-modal-actions">
          <button type="button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className="data-browser-danger-btn"
            onClick={() => void onConfirm()}
            disabled={!canConfirm}
          >
            {loading
              ? "Deleting…"
              : isAll
                ? `Yes, empty ${entity}`
                : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetDbModal({ onClose, onSuccess }: ResetDbModalProps): JSX.Element {
  const [typed, setTyped] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<{
    code: string | null;
    message: string;
  } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, loading]);

  const canConfirm = typed === "RESET" && !loading;

  const onConfirm = async (): Promise<void> => {
    if (!canConfirm) return;
    setLoading(true);
    setErr(null);
    try {
      const result = await api.devReset();
      onSuccess(result);
    } catch (e) {
      if (e instanceof ApiError) {
        // Production builds surface the endpoint as 404 route_not_found;
        // re-message that case so it's clear what happened.
        const friendly =
          e.code === "route_not_found"
            ? "This endpoint is disabled in production builds."
            : e.message;
        setErr({ code: e.code, message: friendly });
      } else {
        setErr({ code: null, message: (e as Error).message });
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(loading ? () => undefined : onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-db-modal-title"
      >
        <h2 id="reset-db-modal-title" className="row-edit-modal-title">
          Reset to E-Pulsive baseline
        </h2>
        <p className="row-edit-modal-note">
          <strong>Kept:</strong> the studio, trainers (+ memory &amp;
          availability), all config (studio config, secrets, staff allowlist,
          marketing-flow overrides, tags), the Gmail + inbox scan watermarks,
          website analytics, Instagram threads, the{" "}
          <strong>whole PR pipeline</strong> (contacts, engagements,
          timeline), the <strong>whole operator inbox</strong> (threads,
          timeline, correspondents and their email conversations) and{" "}
          <strong>Peter&rsquo;s + the inbox agent&rsquo;s PA actions</strong>.
          <br />
          <strong>Deleted:</strong> every member and their bookings, payments,
          slots, memberships, packages and vendor links; all conversations and
          message / LLM logs; the audit + saga churn; and the retired
          deep-worker &amp; Eladio PA stack including its task board.
          <br />
          This is a <strong>hard delete</strong>, not an anonymise — the data
          does not come back. Supabase Auth + L2 YAML are untouched (no
          re-login). Type <code>RESET</code> to confirm.
        </p>
        <div className="row-edit-modal-form">
          <label
            className="row-edit-modal-label"
            htmlFor="reset-db-modal-confirm"
          >
            <span>Type RESET to confirm</span>
            <input
              id="reset-db-modal-confirm"
              type="text"
              value={typed}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        </div>
        {err !== null ? (
          <div className="row-edit-modal-error">
            <strong>Error:</strong>{" "}
            {err.code !== null ? (
              <code className="tag tag-error">{err.code}</code>
            ) : null}
            <p>{err.message}</p>
          </div>
        ) : null}
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="row-edit-modal-save"
            disabled={!canConfirm}
            onClick={() => void onConfirm()}
          >
            {loading ? "Resetting…" : "Reset database"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PurgeMembersModal — studio-scoped member purge (member-purge.ts). Typed
// `PURGE` to confirm; same protocol as ResetDbModal / delete_member.
// ---------------------------------------------------------------------------

function PurgeMembersModal({
  onClose,
  onSuccess,
}: PurgeMembersModalProps): JSX.Element {
  const [typed, setTyped] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<{
    code: string | null;
    message: string;
  } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, loading]);

  const canConfirm = typed === "PURGE" && !loading;

  const onConfirm = async (): Promise<void> => {
    if (!canConfirm) return;
    setLoading(true);
    setErr(null);
    try {
      const result = await api.purgeMembers();
      onSuccess(result);
    } catch (e) {
      if (e instanceof ApiError) {
        const friendly =
          e.code === "route_not_found"
            ? "Member purge is disabled on this deployment (LAYER_ALLOW_MEMBER_PURGE is not set)."
            : e.message;
        setErr({ code: e.code, message: friendly });
      } else {
        setErr({ code: null, message: (e as Error).message });
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="row-edit-modal-backdrop"
      {...useBackdropDismiss(loading ? () => undefined : onClose)}
      role="presentation"
    >
      <div
        className="row-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="purge-members-modal-title"
      >
        <h2 id="purge-members-modal-title" className="row-edit-modal-title">
          Purge every member of this studio
        </h2>
        <p className="row-edit-modal-note">
          <strong>Kept:</strong> the studio, trainers (+ memory &amp;
          availability), <strong>every slot</strong>, all config (studio
          config, secrets, staff allowlist, MCP client keys, flow overrides,
          tags, templates, brand assets), the audit trail, the{" "}
          <strong>whole PR pipeline</strong> and the{" "}
          <strong>whole operator inbox</strong> bar member-owned threads.
          <br />
          <strong>Deleted:</strong> every member of THIS studio and their
          bookings, payments, packages, memberships, conversations, WhatsApp /
          email / chat messages and LLM traces, session notes, memory, tags,
          access codes, channel bindings, pending proposals, lead captures and
          the saga journal of member actions. Tasks assigned to a member are
          unassigned, not deleted.
          <br />
          This is a <strong>hard delete</strong> scoped to your studio — the
          data does not come back. Type <code>PURGE</code> to confirm.
        </p>
        <div className="row-edit-modal-form">
          <label
            className="row-edit-modal-label"
            htmlFor="purge-members-modal-confirm"
          >
            <span>Type PURGE to confirm</span>
            <input
              id="purge-members-modal-confirm"
              type="text"
              value={typed}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        </div>
        {err !== null ? (
          <div className="row-edit-modal-error">
            <strong>Error:</strong>{" "}
            {err.code !== null ? (
              <code className="tag tag-error">{err.code}</code>
            ) : null}
            <p>{err.message}</p>
          </div>
        ) : null}
        <div className="row-edit-modal-actions">
          <button
            type="button"
            className="row-edit-modal-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="row-edit-modal-save"
            disabled={!canConfirm}
            onClick={() => void onConfirm()}
          >
            {loading ? "Purging…" : "Purge members"}
          </button>
        </div>
      </div>
    </div>
  );
}
